import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Image as ImageIcon, Send, Loader2, Sparkles, Calendar, AlertCircle, CheckCircle2, Repeat, Eye, X, Mic, MicOff, MessageSquare, Film, Play } from 'lucide-react';
import { getCurrentUser } from '../services/auth';
import { neonQuery, callClaude } from '../services/api';
import MediaPicker from '../components/MediaPicker';
import ImageCropModal from '../components/ImageCropModal';
import { uploadVideoToLibrary, isVideoFile } from '../lib/uploadVideoToLibrary';
import cpLogo from '../logo-cp.svg';
import organicFetch from '../lib/organicFetch';

const SITE_URL = 'https://cpx-prototype.netlify.app';
const CAPTION_MODEL = 'claude-sonnet-4-6';

// Saved one-click prefills for the AI caption assistant. Click → drops the text
// into the prompt box, ready to Generate. Add more here as Greg finds keepers.
const CAPTION_PRESETS = [
  {
    label: 'Real people, real food',
    text: "We're running a series showing happy people with a Crab Place box or with seafood. These are real customers and real photos. Competitors use fake AI to create these shots; we want to tout that our products and our people are real. After 30 years of building this business, with great product in-house and great talent to photograph it, this is what you get with us. Write a caption that reflects these real people enjoying their experience with CrabPlace.com and encourages the viewer to start planning their next world-class seafood feast themselves.",
  },
];

function slug(s) {
  return (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

// Voice dictation button — uses the browser's built-in Web Speech API (Chrome).
// Appends spoken text to whatever's already in the field. Hidden if unsupported.
function MicButton({ value, onChange, title }) {
  const supported = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const recRef = useRef(null);
  const baseRef = useRef('');
  const [listening, setListening] = useState(false);
  useEffect(() => () => { try { recRef.current && recRef.current.stop(); } catch (e) { /* noop */ } }, []);
  if (!supported) return null;
  const toggle = () => {
    if (listening) { try { recRef.current && recRef.current.stop(); } catch (e) { /* noop */ } return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR();
    r.lang = 'en-US'; r.continuous = true; r.interimResults = true;
    baseRef.current = value && value.trim() ? `${value.trim()} ` : '';
    r.onresult = (e) => {
      let txt = '';
      for (let i = 0; i < e.results.length; i++) txt += e.results[i][0].transcript;
      onChange(`${baseRef.current}${txt}`);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    try { r.start(); recRef.current = r; setListening(true); } catch (e) { setListening(false); }
  };
  return (
    <button type="button" onClick={toggle} title={title || 'Dictate with your voice'}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 9px',
        borderRadius: 14, cursor: 'pointer', fontFamily: 'Montserrat',
        background: listening ? '#fdecea' : '#fff', border: `1px solid ${listening ? '#e09b94' : 'var(--gray-200)'}`,
        color: listening ? '#c0392b' : 'var(--navy)' }}>
      {listening ? <MicOff size={12} /> : <Mic size={12} />}{listening ? 'Stop' : 'Dictate'}
    </button>
  );
}

// ── AI "build the whole post" flow (prompt → searches Media Library → proposes
// a full post: image + caption + destination). Mirrors the Paid builder's agent.
const ORG_SEARCH_TOOL = {
  name: 'search_media_library',
  description: "Search The Crab Place's photo library. Returns assets with id, alt_text, suggested_name, tags, quality (1-5), starred, category. Call 1-3 times to find the best image before proposing.",
  input_schema: {
    type: 'object',
    properties: {
      query: { type: 'string', description: 'Natural language, e.g. "family eating steamed crabs"' },
      category: { type: 'string', enum: ['products', 'plated', 'lifestyle', 'ugc', 'process', 'people', 'marketing'] },
      minQuality: { type: 'integer', minimum: 1, maximum: 5 },
      limit: { type: 'integer', minimum: 1, maximum: 12 },
    },
  },
};
const ORG_PROPOSE_TOOL = {
  name: 'propose_post',
  description: 'Submit the final post. Call exactly once after you have chosen an image.',
  input_schema: {
    type: 'object',
    properties: {
      mediaAssetId: { type: 'integer', description: 'A real media_assets.id from your search results.' },
      caption: { type: 'string', description: 'The full Facebook caption.' },
      destinationPath: { type: 'string', description: 'Path on crabplace.com to drive to, e.g. /hard-crabs/ or /crab-cakes/. Use / if unsure.' },
      campaign: { type: 'string', description: 'Short campaign/theme slug for grouping (utm_campaign), e.g. "fathers-day-2026" or "real-people-real-food". Reuse across posts in the same campaign.' },
      postName: { type: 'string', description: 'Short, descriptive, post-specific name (utm_content), e.g. "happy-bret-boat" or "mustard-debate". 2-5 words, hyphen/space ok.' },
    },
    required: ['mediaAssetId', 'caption', 'postName'],
  },
};
const ORG_SYSTEM = `You build organic Facebook posts for The Crab Place (crabplace.com), a Maryland seafood retailer. Given the user's idea: (1) call search_media_library 1-3 times to find the best real photo, (2) call propose_post ONCE with the chosen image id, a warm on-brand caption, a destination path on crabplace.com, a short campaign/theme slug (utm_campaign), and a descriptive post-specific postName (utm_content). Voice: warm, celebratory, Maryland-proud, never salesy. IMPORTANT: NO em dashes or en dashes ever (use periods, commas, parentheses); avoid the "not just X, it's Y" crutch, rule-of-three stacking, and buzzwords (elevate, indulge, crafted, curated). A few relevant emojis are fine. Caption is 1 to 3 short sentences ending with a short call to action. CRITICAL: do NOT write any website address or "crabplace.com" / "crabplace.co" in the caption — a trackable link is added automatically below it, and a bare domain would be an UNTRACKED link. End with something like "Order at the link below." or "Tap the link to start your feast." Pick a real, high-quality image whose alt_text/tags match the idea (prefer starred, then higher quality).`;

async function buildPostWithAI(prompt) {
  const seen = new Map();
  let messages = [{ role: 'user', content: prompt }];
  for (let iter = 0; iter < 5; iter++) {
    const data = await callClaude({ model: CAPTION_MODEL, max_tokens: 1500, system: ORG_SYSTEM, tools: [ORG_SEARCH_TOOL, ORG_PROPOSE_TOOL], messages });
    const blocks = data.content || [];
    const tools = blocks.filter(b => b.type === 'tool_use');
    const prop = tools.find(b => b.name === 'propose_post');
    if (prop) return { proposal: prop.input, seen };
    if (!tools.length) { return { text: (blocks.find(b => b.type === 'text') || {}).text || 'No post proposed.' }; }
    messages = [...messages, { role: 'assistant', content: blocks }];
    const results = [];
    for (const b of tools) {
      if (b.name === 'search_media_library') {
        try {
          const rows = await neonQuery('media_assets_list', {
            search: b.input.query || '', brand: null, category: b.input.category || null, subcategory: null,
            minQuality: b.input.minQuality || 0, usableForAny: [], tagsAll: [], excludeTags: [],  // HEIC uploads are stored as JPEG - 'source-heic' is provenance, not a format problem
            starredOnly: false, includeArchived: false, limit: Math.min(b.input.limit || 8, 12), offset: 0,
          });
          (rows || []).forEach(r => seen.set(r.id, r));
          results.push({ type: 'tool_result', tool_use_id: b.id, content: JSON.stringify((rows || []).map(r => ({ id: r.id, alt_text: r.alt_text, suggested_name: r.suggested_name, tags: r.tags || [], quality: r.quality_score, starred: !!r.human_starred, category: r.category }))) });
        } catch (e) { results.push({ type: 'tool_result', tool_use_id: b.id, content: 'Error: ' + e.message, is_error: true }); }
      } else { results.push({ type: 'tool_result', tool_use_id: b.id, content: 'unknown tool', is_error: true }); }
    }
    messages = [...messages, { role: 'user', content: results }];
  }
  throw new Error('AI loop ended without a proposal');
}
// A publish call that outlives Netlify's ~26s synchronous window gets an HTML
// "Inactivity Timeout" page back instead of JSON (the function usually still
// finishes server-side). Parse defensively so the user sees a real message,
// never "Unexpected token '<' ... is not valid JSON".
async function readJsonSafe(res) {
  const text = await res.text();
  try { return JSON.parse(text); } catch (e) { return null; }
}
function assetUrl(a) {
  if (!a) return null;
  if (a.__externalUrl) return a.__externalUrl;   // pasted image URL (not a Library asset)
  if (a.__localUrl) return a.__localUrl;          // dropped file object URL
  const key = a.r2_original_key || a.r2_thumb_key;
  return key ? `${SITE_URL}/.netlify/functions/r2-image?key=${encodeURIComponent(key)}` : null;
}
// Parse a pasted Media Library "Copy" URL back into an asset so it flows through
// the same crop/publish pipeline as a picked asset; a non-Library URL is kept
// verbatim as an external image.
const VIDEO_URL_RE = /\.(mp4|mov|m4v|webm|avi)(\?|$)/i;
function assetFromUrl(raw) {
  const url = String(raw || '').trim();
  if (!url) return null;
  try {
    const u = new URL(url, SITE_URL);
    const key = u.searchParams.get('key');
    if (key) return { r2_original_key: key, suggested_name: key.split('/').pop(), __fromUrl: true };
    return { __externalUrl: url, suggested_name: (u.pathname.split('/').pop() || url), __fromUrl: true };
  } catch (e) { return null; }
}
// Downscale a dropped image to a base64 JPEG (long edge <= maxEdge).
async function imageToBase64(file, maxEdge, quality) {
  const dataUrl = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = () => rej(new Error('Could not read file')); fr.readAsDataURL(file); });
  const img = await new Promise((res, rej) => { const el = new Image(); el.onload = () => res(el); el.onerror = () => rej(new Error('Could not decode image (convert HEIC/RAW to JPEG first)')); el.src = dataUrl; });
  const w = img.naturalWidth, h = img.naturalHeight;
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  const cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
  const canvas = document.createElement('canvas'); canvas.width = cw; canvas.height = ch;
  canvas.getContext('2d').drawImage(img, 0, 0, cw, ch);
  return { base64: canvas.toDataURL('image/jpeg', quality).split(',')[1], naturalWidth: w, naturalHeight: h };
}
async function fileToJpegFile(file, maxEdge, quality) {
  const { base64 } = await imageToBase64(file, maxEdge, quality);
  const bin = atob(base64); const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  return new File([arr], `${(file.name || 'image').replace(/\.[^.]+$/, '')}.jpg`, { type: 'image/jpeg' });
}
// HEIC/HEIF can't be canvas-decoded in the browser — read the raw bytes so the
// server can convert to JPEG (heic-convert), same as the Media Library uploader.
const isHeicFile = (f) => /image\/hei[cf]/i.test(f.type || '') || /\.(heic|heif)$/i.test(f.name || '');
async function fileToRawBase64(file) {
  const dataUrl = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = () => rej(new Error('Could not read file')); fr.readAsDataURL(file); });
  return String(dataUrl).slice(String(dataUrl).indexOf(',') + 1);
}
// Poster (thumb) image for any asset — used as a video's preview frame.
function posterUrl(a) {
  return a && a.r2_thumb_key ? `${SITE_URL}/.netlify/functions/r2-image?key=${encodeURIComponent(a.r2_thumb_key)}` : null;
}
// Raw-bytes URL for a Media Library VIDEO. r2-image base64-buffers + caps at ~6MB
// (502s on video); r2-fetch 302s to a presigned R2 URL that streams. We store this
// stable URL — the backend resolves the redirect to a direct URL for Facebook at
// publish time (so scheduled video posts still work).
function videoFetchUrl(a) {
  return a && a.r2_original_key ? `${SITE_URL}/.netlify/functions/r2-fetch?key=${encodeURIComponent(a.r2_original_key)}` : null;
}
// "Preview" — a close mock-up of how the composed post will render in the
// Facebook desktop feed, BEFORE it goes out. Photo/video posts render from the
// already-picked media; LINK posts ask the server (link-preview action) to fetch
// the destination the way Facebook's crawler does and report which share image
// Facebook will actually use — including a warning when the page lists share
// images FB can't read (404 / too small), which is how blog posts used to fall
// back to the navy default card. FB's own styling (system fonts, #1877F2 blue,
// #65676B gray) is used deliberately so the mock reads like the real feed.
const FB_FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
function FbPreviewModal({ brand, caption, postKind, photos, videoAsset, sourceVideo, fbCover, destinationUrl, hasLink, onClose }) {
  const isLink = postKind === 'link';
  const [card, setCard] = useState(null);      // link-preview response
  const [cardState, setCardState] = useState(isLink ? 'loading' : 'idle');
  useEffect(() => {
    if (!isLink || !destinationUrl) return;
    let dead = false;
    (async () => {
      try {
        const r = await organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'link-preview', url: destinationUrl }) });
        const d = await readJsonSafe(r);
        if (dead) return;
        if (d && !d.error) { setCard(d); setCardState('ok'); }
        else { setCard(d || null); setCardState('error'); }
      } catch (e) { if (!dead) setCardState('error'); }
    })();
    return () => { dead = true; };
  }, [isLink, destinationUrl]);

  const pageName = brand === 'crabandcruise' ? 'Crab & Cruise' : 'CrabPlace.com';
  const photoSrc = (p) => p.preview || assetUrl(p.asset);
  const gridImgs = photos.slice(0, 4);
  const extra = photos.length - 4;
  const cell = (src, key, style, overlay) => (
    <div key={key} style={{ position: 'relative', background: '#000', overflow: 'hidden', ...style }}>
      <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      {overlay}
    </div>
  );
  const plusOverlay = extra > 0 ? (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 26, fontWeight: 600, fontFamily: FB_FONT }}>+{extra}</div>
  ) : null;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(0,0,0,0.82)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px' }}>
      <button type="button" onClick={onClose} aria-label="Close preview"
        style={{ position: 'fixed', top: 18, right: 22, background: 'rgba(255,255,255,0.14)', color: '#fff', border: 'none', borderRadius: '50%', width: 38, height: 38, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <X size={20} />
      </button>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 500, maxWidth: '100%' }}>
        <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.85)', fontSize: 12, marginBottom: 10, fontFamily: 'Montserrat' }}>
          How this post will look in the Facebook feed
        </div>
        <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.2)', fontFamily: FB_FONT, overflow: 'hidden' }}>
          {/* Page header */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '12px 16px 4px' }}>
            <span style={{ width: 40, height: 40, borderRadius: '50%', background: '#00395C', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <img src={cpLogo} alt="" style={{ width: 26, height: 26, filter: 'brightness(0) invert(1)' }} />
            </span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#050505', lineHeight: 1.2 }}>{pageName}</div>
              <div style={{ fontSize: 13, color: '#65676B' }}>Just now · <span title="Public">🌐</span></div>
            </div>
          </div>
          {/* Caption */}
          {(caption || (hasLink && !isLink)) && (
            <div style={{ padding: '6px 16px 10px', fontSize: 15, color: '#050505', whiteSpace: 'pre-wrap', lineHeight: 1.35 }}>
              {caption}
              {hasLink && (
                <div style={{ marginTop: caption ? 8 : 0 }}>
                  <span style={{ color: '#216FDB' }}>crabplace.co/••••</span>
                  <span style={{ color: '#65676B', fontSize: 12 }}> (short link created at publish)</span>
                </div>
              )}
            </div>
          )}
          {/* Media */}
          {isLink ? (
            <div style={{ borderTop: '1px solid #dadde1', borderBottom: '1px solid #dadde1' }}>
              {cardState === 'loading' && (
                <div style={{ padding: 30, textAlign: 'center', color: '#65676B', fontSize: 13, background: '#f0f2f5' }}>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: 6 }} />
                  Fetching the link the way Facebook will…
                </div>
              )}
              {cardState === 'error' && (
                <div style={{ padding: 16, color: '#9a3b00', fontSize: 13, background: '#fff4ec' }}>
                  {card?.error || 'Could not fetch the link preview.'}
                </div>
              )}
              {cardState === 'ok' && card && (
                <>
                  {card.image && card.image.large && (
                    <div style={{ width: '100%', height: 261, background: '#000', overflow: 'hidden' }}>
                      <img src={card.image.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    </div>
                  )}
                  <div style={{ background: '#f0f2f5', padding: '10px 12px', display: 'flex', gap: 12, alignItems: 'center' }}>
                    {card.image && !card.image.large && (
                      <img src={card.image.url} alt="" style={{ width: 100, height: 100, objectFit: 'cover', flexShrink: 0, borderRadius: 4 }} />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: '#65676B', textTransform: 'uppercase' }}>{card.domain}</div>
                      <div style={{ fontSize: 17, fontWeight: 600, color: '#050505', lineHeight: 1.25, margin: '3px 0' }}>{card.title}</div>
                      {card.description && <div style={{ fontSize: 15, color: '#65676B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{card.description}</div>}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : videoAsset || sourceVideo ? (
            <div style={{ position: 'relative', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220, maxHeight: 500, overflow: 'hidden' }}>
              {(fbCover && fbCover.preview) || posterUrl(videoAsset) ? (
                <img src={(fbCover && fbCover.preview) || posterUrl(videoAsset)} alt="" style={{ width: '100%', maxHeight: 500, objectFit: 'contain', display: 'block' }} />
              ) : (
                <div style={{ color: '#888', fontSize: 13, padding: 60, fontFamily: FB_FONT }}>{sourceVideo?.title || 'Video'}</div>
              )}
              <div style={{ position: 'absolute', width: 56, height: 56, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                <div style={{ width: 0, height: 0, borderTop: '11px solid transparent', borderBottom: '11px solid transparent', borderLeft: '18px solid #fff', marginLeft: 4 }} />
              </div>
            </div>
          ) : photos.length > 0 ? (
            photos.length === 1 ? (
              <div style={{ background: '#000', maxHeight: 500, overflow: 'hidden' }}>
                <img src={photoSrc(photos[0])} alt="" style={{ width: '100%', maxHeight: 500, objectFit: 'contain', display: 'block' }} />
              </div>
            ) : photos.length === 2 ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {gridImgs.slice(0, 2).map((p, i) => cell(photoSrc(p), i, { height: 250 }))}
              </div>
            ) : photos.length === 3 ? (
              <div style={{ display: 'grid', gap: 2 }}>
                {cell(photoSrc(gridImgs[0]), 'a', { height: 280 })}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  {gridImgs.slice(1, 3).map((p, i) => cell(photoSrc(p), i, { height: 180 }))}
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {gridImgs.map((p, i) => cell(photoSrc(p), i, { height: 185 }, i === 3 ? plusOverlay : null))}
              </div>
            )
          ) : null}
          {/* Action bar */}
          <div style={{ display: 'flex', borderTop: '1px solid #dadde1', margin: '0 12px', padding: '4px 0' }}>
            {['Like', 'Comment', 'Share'].map((l) => (
              <div key={l} style={{ flex: 1, textAlign: 'center', padding: '8px 0', color: '#65676B', fontSize: 14, fontWeight: 600 }}>
                {l === 'Like' ? '👍' : l === 'Comment' ? '💬' : '↗'} {l}
              </div>
            ))}
          </div>
        </div>
        {/* Diagnostics under the mock */}
        {isLink && cardState === 'ok' && card && !card.image && (
          <div style={{ marginTop: 10, padding: '9px 12px', background: '#fdecea', border: '1px solid #f5c6c0', borderRadius: 6, fontSize: 12, color: '#8a2a1b', fontFamily: 'Montserrat' }}>
            Facebook can't find a usable share image on this page — the post would go out with no picture (or a stale cached one). Fix the page's share image before posting.
          </div>
        )}
        {isLink && cardState === 'ok' && card && card.image && card.skipped && card.skipped.length > 0 && (
          <div style={{ marginTop: 10, padding: '9px 12px', background: '#fff8ef', border: '1px solid #ffd9a8', borderRadius: 6, fontSize: 12, color: '#9a5b00', fontFamily: 'Montserrat' }}>
            Heads up: this page lists {card.skipped.length} share image{card.skipped.length > 1 ? 's' : ''} Facebook can't use ({card.skipped.map(s => s.reason).join('; ')}) — the card above shows the one Facebook will actually pick.
          </div>
        )}
        <div style={{ marginTop: 10, textAlign: 'center', color: 'rgba(255,255,255,0.55)', fontSize: 11, fontFamily: 'Montserrat' }}>
          Close mock-up — exact fonts and crops can differ slightly. If Facebook shows an OLD image for a link you just fixed, re-scrape it in the Sharing Debugger first.
        </div>
      </div>
    </div>
  );
}
// Selected-video preview. A live <video> element paints BLACK for .MOV/HEVC phone
// clips Chrome can't decode inline (even with a valid poster), so default to the
// poster thumbnail as an <img> (same reliable path the picker grid uses) with a
// play badge; only mount the <video> when the user actually clicks to play.
function VideoPreview({ asset }) {
  const [play, setPlay] = useState(false);
  const poster = posterUrl(asset);
  const src = videoFetchUrl(asset);
  const box = { width: 200, maxHeight: 200, borderRadius: 6, border: '1px solid var(--gray-200)', background: '#000' };
  if (play && src) {
    return <video src={src} poster={poster || undefined} controls autoPlay preload="metadata" style={box} />;
  }
  return (
    <div onClick={() => src && setPlay(true)} title={src ? 'Click to play' : ''}
      style={{ ...box, position: 'relative', cursor: src ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {poster
        ? <img src={poster} alt="" style={{ width: '100%', maxHeight: 200, objectFit: 'contain', display: 'block' }} />
        : <Film size={40} color="#fff" style={{ opacity: 0.6 }} />}
      <div style={{ position: 'absolute', width: 44, height: 44, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
        <div style={{ width: 0, height: 0, borderTop: '9px solid transparent', borderBottom: '9px solid transparent', borderLeft: '15px solid #fff', marginLeft: 3 }} />
      </div>
    </div>
  );
}
// Pick the exact frame Facebook shows before the video plays. Streams the video
// (crossOrigin=anonymous — the R2 bucket serves CORS for this origin, verified),
// pause/seek to a frame, "Use this frame" draws it to a canvas at native
// resolution and hands back a JPEG File + preview URL. FB's thumb param needs
// real image bytes (unlike IG's thumb_offset), hence the capture.
function FbThumbModal({ src, onSet, onClose }) {
  const vidRef = useRef(null);
  const [err, setErr] = useState(null);
  const modalBtn = { background: '#fff', color: 'var(--navy)', border: '1px solid var(--gray-200)', borderRadius: 6, fontWeight: 600, fontSize: 11, cursor: 'pointer', fontFamily: 'Montserrat', padding: '6px 10px' };
  const capture = () => {
    const v = vidRef.current;
    if (!v || !v.videoWidth) { setErr('The video has not loaded a frame yet — press play, then pause on the frame you want.'); return; }
    try {
      const canvas = document.createElement('canvas');
      canvas.width = v.videoWidth; canvas.height = v.videoHeight;
      canvas.getContext('2d').drawImage(v, 0, 0);
      canvas.toBlob((blob) => {
        if (!blob) { setErr('Could not capture that frame.'); return; }
        const file = new File([blob], 'fb-thumb.jpg', { type: 'image/jpeg' });
        onSet({ file, preview: URL.createObjectURL(file) });
        onClose();
      }, 'image/jpeg', 0.88);
    } catch (e) {
      setErr('Could not capture the frame (the browser blocked reading the video pixels). Try a different clip or skip the custom thumbnail.');
    }
  };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#1c1c1e', borderRadius: 10, padding: 14, maxWidth: 460, width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <video ref={vidRef} src={src} crossOrigin="anonymous" controls autoPlay playsInline
          onError={() => setErr('Could not load the video preview.')}
          style={{ width: '100%', maxHeight: '60vh', borderRadius: 6, background: '#000' }} />
        {err ? <div style={{ color: '#ff9f8a', fontSize: 12 }}>{err}</div> : null}
        <div style={{ fontSize: 11.5, color: '#d1d1d6' }}>
          Pause or seek to the frame you want Facebook to show, then capture it.
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button style={modalBtn} onClick={capture}>Use this frame as thumbnail</button>
          <button style={{ ...modalBtn, background: 'var(--navy)', color: '#fff', border: '1px solid var(--navy)' }} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

const VIDEO_EXT_RE = /\.(mov|mp4|m4v|webm|avi|mkv)$/i;
const isVideoAsset = (a) => a?.media_kind === 'video' || VIDEO_EXT_RE.test(String(a?.r2_original_key || ''));

// Viral-video titles are long emoji-laden captions. Strip emoji + punctuation
// and drop stopwords/short words, returning up to `max` significant lowercase
// keywords for a Media Library search.
const TITLE_STOPWORDS = new Set(['the', 'and', 'for', 'with', 'you', 'your', 'our', 'this', 'that', 'from', 'get', 'all', 'are', 'was', 'has', 'out', 'now', 'new', 'how', 'why', 'who', 'not', 'but', 'its', 'into', 'over', 'they', 'them', 'when', 'what', 'will', 'just', 'like', 'have']);
function titleKeywords(title, max = 6) {
  return (title || '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ') // drops emoji + punctuation, keeps letters/numbers
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length >= 3 && !TITLE_STOPWORDS.has(w))
    .slice(0, max);
}
// Search the Media Library for a likely ORIGINAL (audio-carrying) video matching
// a viral video's title. media_assets_list ANDs every search term, so a long
// caption rarely matches — cascade from more keywords to fewer until a hit.
// Returns [] (never throws) so a search failure can't break the page.
async function findOriginalVideo(title) {
  const words = titleKeywords(title, 6);
  if (!words.length) return [];
  const attempts = [];
  if (words.length >= 5) attempts.push(words.slice(0, 6));
  if (words.length >= 3) attempts.push(words.slice(0, 3));
  attempts.push(words.slice(0, 2));
  const tried = new Set();
  for (const terms of attempts) {
    const q = terms.join(' ').trim();
    if (!q || tried.has(q)) continue;
    tried.add(q);
    try {
      const rows = await neonQuery('media_assets_list', {
        search: q, brand: null, category: null, subcategory: null,
        minQuality: 0, usableForAny: [], tagsAll: [], excludeTags: [],  // HEIC uploads are stored as JPEG - 'source-heic' is provenance, not a format problem
        starredOnly: false, includeArchived: false, mediaKind: 'video',
        orderBy: 'quality', limit: 6, offset: 0,
      });
      const vids = (rows || []).filter(isVideoAsset);
      if (vids.length) return vids.slice(0, 3);
    } catch (e) { console.warn('[organic] Media Library original-video search failed', e); }
  }
  return [];
}
// Same-origin (relative) + downsampled URL for the crop modal: r2-resize avoids
// canvas taint and the ~6MB r2-image cap on huge Media Library masters.
function cropSourceUrl(a) {
  if (!a) return null;
  if (a.__externalUrl) return a.__externalUrl;
  if (a.__localUrl) return a.__localUrl;
  const key = a.r2_original_key || a.r2_thumb_key;
  return key ? `/.netlify/functions/r2-resize?key=${encodeURIComponent(key)}&edge=2400` : null;
}
// Organic orders are only trackable when the link lands on crabplace.com (where the
// GTM capture tag + cp_attrib cookie live). Organic posts get NO fbclid, so UTMs are
// the only signal — keep the destination on our own site.
function destinationIsTracked(url) {
  try { const h = new URL(url).hostname.toLowerCase(); return h === 'crabplace.com' || h.endsWith('.crabplace.com'); }
  catch { return false; }
}
function fmtViews(n) {
  const v = parseInt(n || 0, 10);
  if (v >= 1e6) return (v / 1e6).toFixed(v >= 1e7 ? 0 : 1) + 'M';
  if (v >= 1e3) return (v / 1e3).toFixed(v >= 1e4 ? 0 : 1) + 'K';
  return v.toLocaleString();
}
function fmtDate(d) { if (!d) return '—'; try { return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return '—'; } }
// m:ss clock for a caption segment's start/end (seconds).
function fmtClock(sec) { const t = Math.max(0, Math.floor(Number(sec) || 0)); const m = Math.floor(t / 60); const s = t % 60; return `${m}:${String(s).padStart(2, '0')}`; }

// Build timed caption segments from TYPED text — for b-roll / silent clips with
// no speech to transcribe: burns YOUR words onto the video. Strips links + emojis
// (the caption font has no emoji glyphs), splits into short readable chunks, and
// spreads them evenly across the video's duration.
function captionsFromText(text, durationSec) {
  const clean = String(text || '')
    .replace(/https?:\/\/\S+/g, '')
    .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}\u{FE0F}\u{200D}]/gu, '')
    .replace(/\s+/g, ' ').trim();
  if (!clean) return [];
  const dur = Number(durationSec) > 0 ? Number(durationSec) : 15;
  const sentences = clean.match(/[^.!?]+[.!?]*/g) || [clean];
  const chunks = [];
  for (const s of sentences) {
    let t = s.trim();
    while (t.length > 64) {
      let bp = t.lastIndexOf(' ', 64); if (bp < 30) bp = 64;
      chunks.push(t.slice(0, bp).trim()); t = t.slice(bp).trim();
    }
    if (t) chunks.push(t);
  }
  if (!chunks.length) return [];
  const per = dur / chunks.length;
  return chunks.map((c, i) => ({ start: +(i * per).toFixed(2), end: +Math.min(dur, (i + 1) * per).toFixed(2), text: c }));
}

const CAPTION_FONTS = [{ k: 'anton', label: 'Anton (bold display)' }, { k: 'montserrat', label: 'Montserrat (brand)' }, { k: 'oswald', label: 'Oswald (condensed)' }, { k: 'bebas', label: 'Bebas Neue (tall caps)' }];
const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid var(--gray-200)', borderRadius: 6, fontFamily: 'Montserrat', color: 'var(--navy)', boxSizing: 'border-box' };
const primaryBtn = { background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'Montserrat' };
const ghostBtn = { background: '#fff', color: 'var(--navy)', border: '1px solid var(--gray-200)', borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'Montserrat', padding: '6px 12px' };
const miniBtn = { background: '#fff', color: 'var(--navy)', border: '1px solid var(--gray-200)', borderRadius: 4, fontWeight: 600, fontSize: 10, cursor: 'pointer', fontFamily: 'Montserrat', padding: '2px 6px', lineHeight: 1.4 };

function Card({ title, right, children }) {
  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{title}</span>{right}
      </div>
      <div className="card-body" style={{ padding: 16 }}>{children}</div>
    </div>
  );
}
function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

const BRANDS = [{ slug: 'crabplace', name: 'Crab Place' }, { slug: 'crabandcruise', name: 'Crab & Cruise' }];

// Quick destination-link presets (Crab Place product pages, verified live).
const PRODUCT_LINKS = [
  { label: 'Steamed Crabs', path: '/steamed-maryland-blue-crabs/' },
  { label: 'Crab Cakes', path: '/crab-cakes/' },
  { label: 'Crab Meat', path: '/crab-meat/' },
  { label: 'Lobster Tails', path: '/maine-lobster-tails/' },
  { label: 'Live Crabs', path: '/live-maryland-blue-crabs/' },
];

export default function OrganicBuilder() {
  const user = getCurrentUser();
  const [brand, setBrand] = useState('crabplace'); // Crab Place / Crab & Cruise

  // Compose state
  const [photos, setPhotos] = useState([]);          // [{ asset, file, preview }] — 1 = single, 2+ = FB grid
  const [videoAsset, setVideoAsset] = useState(null); // a Media Library video → native FB video post
  const [noSound, setNoSound] = useState(false);      // strip the audio track → post plays silently
  // Captions: transcribe → edit → burn styled captions into the video so it
  // reads muted. Pairs with "Post without sound".
  const [captionsOn, setCaptionsOn] = useState(false);
  const [captionSegments, setCaptionSegments] = useState(null); // [{ start, end, text }]
  const [captionState, setCaptionState] = useState('idle');     // idle | loading | ready | error
  const [captionErr, setCaptionErr] = useState(null);
  const [captioning, setCaptioning] = useState(false);          // "adding captions…" during publish
  const [captionFont, setCaptionFont] = useState('anton');
  const [mediaType, setMediaType] = useState('photo'); // photo | video | none
  const [postKind, setPostKind] = useState('media');   // 'media' (photo/video) | 'link' (blog/page — FB shows a preview card)
  const [mediaPick, setMediaPick] = useState('photo'); // which Pick-media button is active: 'photo' | 'video' (filters the picker)
  const [storyMode, setStoryMode] = useState(false); // FB Page Story mode — multi-frame, each frame posts as its own Story
  const [urlInput, setUrlInput] = useState('');        // paste a Media Library "Copy" URL to add an image
  const [dragActive, setDragActive] = useState(false);
  const [dropBusy, setDropBusy] = useState(false);
  const [saveToLibrary, setSaveToLibrary] = useState(true); // save dropped files to the Media Library
  // Custom FB video thumbnail: a frame captured from the video (JPEG File +
  // object-URL preview). Uploaded to R2 at publish time like photo crops.
  const [fbCover, setFbCover] = useState(null);        // { file, preview } | null
  const [thumbModalOpen, setThumbModalOpen] = useState(false);
  function clearFbCover() {
    setFbCover((prev) => { if (prev && prev.preview) { try { URL.revokeObjectURL(prev.preview); } catch (e) { /* noop */ } } return null; });
  }
  const [caption, setCaption] = useState('');
  const [destinationUrl, setDestinationUrl] = useState('https://crabplace.com/');
  const [campaign, setCampaign] = useState('');       // utm_campaign theme
  const [postName, setPostName] = useState('');       // utm_content
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [sourceVideo, setSourceVideo] = useState(null); // { id, title } when repurposing a viral video
  const [repurposeAudio, setRepurposeAudio] = useState(null); // null | 'checking' | 'ok' | 'muted' | 'unknown' — does FB's copy carry audio?
  // When FB's copy is SILENT, we auto-search the Media Library for the uploaded
  // original (which usually HAS audio) and offer to swap it in.
  // null | { state: 'searching' | 'found' | 'none', candidates: [] }
  const [origSuggest, setOrigSuggest] = useState(null);
  const [watchUrl, setWatchUrl] = useState(null);       // signed FB source URL for the preview modal (fetched fresh per click)
  const [watchLoading, setWatchLoading] = useState(false);
  const [watchError, setWatchError] = useState(false);  // true → show "preview unavailable" inline note
  // Unique token appended to utm_content so two posts never collide (even with
  // the same name). Stable while composing; regenerated after each publish.
  const [uid, setUid] = useState(() => Math.random().toString(36).slice(2, 6));

  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false); // FB-style post preview modal
  // Each photo carries its own crop (File + instant preview); crops upload to R2
  // DURING publish so they always apply (no race). cropIdx = photo being cropped.
  const [cropIdx, setCropIdx] = useState(null);
  const [posting, setPosting] = useState(false);
  const [stripping, setStripping] = useState(false);  // "removing audio…" while the silent copy renders
  const [postMsg, setPostMsg] = useState(null);       // { ok, text }

  // AI caption
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiOptions, setAiOptions] = useState(null);   // array of caption strings
  const [aiErr, setAiErr] = useState(null);
  const [aiBuilding, setAiBuilding] = useState(false);
  const [savedPresets, setSavedPresets] = useState([]);

  // Viral library + history
  const [videos, setVideos] = useState(null);
  const [videosState, setVideosState] = useState('idle'); // idle|loading|ok|needsPermission|error
  const [videosMsg, setVideosMsg] = useState(null);
  const [overpostDays, setOverpostDays] = useState(30);
  // Per-row "will the repost have sound?" flag. FB's API often hands back a
  // video-only copy (common for Reels), so a repost goes out silent. We probe
  // each viral video's downloadable source up front (video-audio-probe) so the
  // flag shows in the list without having to click Repurpose to find out.
  const [videoAudio, setVideoAudio] = useState({}); // { [videoId]: 'checking'|'ok'|'muted'|'unknown' }

  const link = (() => {
    if (!destinationUrl) return '';
    try {
      const u = new URL(destinationUrl);
      u.searchParams.set('utm_source', 'facebook');
      u.searchParams.set('utm_medium', 'organic_social');
      if (campaign) u.searchParams.set('utm_campaign', slug(campaign));
      // utm_content is always unique (name + token) so it never collides.
      u.searchParams.set('utm_content', `${slug(postName) || 'post'}-${uid}`);
      return u.toString();
    } catch { return destinationUrl; }
  })();
  const tracked = destinationIsTracked(destinationUrl);

  const loadVideos = useCallback(() => {
    setVideosState('loading');
    organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'videos', brand }) })
      .then(r => r.json()).then(d => {
        if (d.needsPermission) { setVideosState('needsPermission'); setVideosMsg(d.message); return; }
        setVideos(d.videos || []); setOverpostDays(d.overpostDays || 30); setVideosState('ok');
      }).catch(e => { setVideosState('error'); setVideosMsg(e.message); });
  }, [brand]);

  const loadPresets = useCallback(() => {
    organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'presets-list' }) })
      .then(r => r.json()).then(d => setSavedPresets(Array.isArray(d.presets) ? d.presets : [])).catch(() => {});
  }, []);

  // Probe one video's audio, caching the result in sessionStorage so re-loads
  // (and brand switches back) don't re-download the source every time.
  const AUDIO_CACHE_KEY = 'cpxOrganicVideoAudio:v1';
  const probeVideoAudio = useCallback(async (videoId) => {
    const ck = `${brand}:${videoId}`;
    let cache = {};
    try { cache = JSON.parse(sessionStorage.getItem(AUDIO_CACHE_KEY) || '{}'); } catch (e) { /* storage off */ }
    if (cache[ck] && cache[ck] !== 'unknown') { setVideoAudio((s) => ({ ...s, [videoId]: cache[ck] })); return; }
    setVideoAudio((s) => (s[videoId] === 'ok' || s[videoId] === 'muted' ? s : { ...s, [videoId]: 'checking' }));
    let status = 'unknown';
    try {
      const r = await organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'video-audio-probe', videoId, brand }) });
      const d = await r.json();
      status = d.ok && d.hasSource ? (d.hasAudio ? 'ok' : 'muted') : 'unknown';
    } catch (e) { status = 'unknown'; }
    setVideoAudio((s) => ({ ...s, [videoId]: status }));
    try { cache[ck] = status; sessionStorage.setItem(AUDIO_CACHE_KEY, JSON.stringify(cache)); } catch (e) { /* storage off */ }
  }, [brand]);

  // Fetch a fresh signed FB source URL for the repurpose preview and open the
  // player modal. FB source URLs are short-lived, so we always fetch on click and
  // never cache the URL. Works for silent videos too (native player just plays it).
  const watchSourceVideo = useCallback(async (videoId) => {
    if (!videoId) return;
    setWatchError(false); setWatchLoading(true);
    try {
      const r = await organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'video-source', videoId, brand }) });
      const d = await r.json();
      if (d.ok && d.url) setWatchUrl(d.url);
      else setWatchError(true);
    } catch (e) { setWatchError(true); }
    finally { setWatchLoading(false); }
  }, [brand]);

  // When the viral list loads, seed cached flags immediately and probe the rest
  // with a small concurrency pool (each probe range-fetches ~3MB of the source).
  // The persistent DB cache is authoritative: each video carries an `audioStatus`
  // ('ok' | 'muted' | null) from the `videos` payload, so already-probed rows
  // render instantly with zero probing. sessionStorage stays as a within-session
  // layer for rows probed live this session.
  useEffect(() => {
    if (videosState !== 'ok' || !videos || !videos.length) return;
    let cancelled = false;
    let cache = {};
    try { cache = JSON.parse(sessionStorage.getItem(AUDIO_CACHE_KEY) || '{}'); } catch (e) { /* storage off */ }
    const seeded = {};
    videos.forEach((v) => {
      // Prefer the DB-persisted status from the payload; fall back to session cache.
      if (v.audioStatus === 'ok' || v.audioStatus === 'muted') { seeded[v.id] = v.audioStatus; return; }
      const c = cache[`${brand}:${v.id}`];
      if (c) seeded[v.id] = c;
    });
    if (Object.keys(seeded).length) setVideoAudio((s) => ({ ...seeded, ...s }));
    // Only probe rows the DB hasn't resolved AND that aren't already resolved this
    // session — the probe also persists to the DB, so next load they arrive seeded.
    const queue = videos
      .filter((v) => !(v.audioStatus === 'ok' || v.audioStatus === 'muted'))
      .map((v) => String(v.id))
      .filter((id) => { const c = cache[`${brand}:${id}`]; return !(c && c !== 'unknown'); });
    let i = 0;
    const worker = async () => { while (!cancelled && i < queue.length) { const id = queue[i++]; await probeVideoAudio(id); } };
    Promise.all(Array.from({ length: Math.min(4, queue.length) }, worker));
    return () => { cancelled = true; };
  }, [videos, videosState, brand, probeVideoAudio]);

  useEffect(() => { loadVideos(); loadPresets(); }, [loadVideos, loadPresets]);
  // Default the destination to the active brand's site when switching brands.
  useEffect(() => { setDestinationUrl(brand === 'crabandcruise' ? 'https://crabandcruise.com/' : 'https://crabplace.com/'); }, [brand]);
  // Reset caption state whenever the selected video changes or is cleared.
  useEffect(() => { setCaptionsOn(false); setCaptionSegments(null); setCaptionState('idle'); setCaptionErr(null); }, [videoAsset?.id]);

  async function savePreset() {
    const text = aiPrompt.trim();
    if (!text) { setAiErr('Type a prompt first, then save it as a prefill.'); return; }
    // eslint-disable-next-line no-alert
    const label = window.prompt('Name this prefill (short label, e.g. "Holiday gift box"):');
    if (!label || !label.trim()) return;
    await organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'preset-add', label: label.trim(), text, createdBy: user?.staffName || user?.id || null }) });
    loadPresets();
  }
  async function deletePreset(id) {
    await organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'preset-delete', id }) });
    loadPresets();
  }

  async function generateCaption() {
    if (!aiPrompt.trim() || aiLoading) return;
    setAiLoading(true); setAiErr(null); setAiOptions(null);
    const ctx = photos[0] ? `\n\nThe attached image is described as: "${photos[0].asset.alt_text || photos[0].asset.suggested_name || ''}" (tags: ${(photos[0].asset.tags || []).join(', ')}).` : '';
    const sys = `You write organic Facebook captions for The Crab Place (crabplace.com), a Maryland seafood retailer. Warm, celebratory, Maryland-proud, never salesy. IMPORTANT brand voice rules: NO em dashes or en dashes ever (use periods, commas, or parentheses); avoid the "not just X, it's Y" crutch, rule-of-three stacking, and buzzwords (elevate, indulge, crafted, curated). A few relevant emojis are fine. 1 to 3 short sentences ending with a short call to action. CRITICAL: do NOT write any website address or "crabplace.com" / "crabplace.co" in the caption — a trackable link is added automatically below it (a bare domain would be an untracked link); end with e.g. "Order at the link below." Also propose tracking labels: "campaign" = a short lowercase-hyphenated theme for this post (utm_campaign, e.g. "fathers-day-2026"); "postName" = a short lowercase-hyphenated description of this specific post (utm_content, e.g. "last-minute-gift-6hr"). Return ONLY a JSON object: {"campaign":"...","postName":"...","captions":["...","...","..."]} with exactly 3 distinct caption strings, no other text.`;
    try {
      const data = await callClaude({ model: CAPTION_MODEL, max_tokens: 1000, system: sys, messages: [{ role: 'user', content: `Write captions for this post idea: ${aiPrompt.trim()}${ctx}` }] });
      const text = (data.content || []).filter(b => b.type === 'text').map(b => b.text).join('');
      let obj = null, caps = null;
      // Preferred shape: { campaign, postName, captions:[...] }. Fall back to a bare array.
      try { obj = JSON.parse(text.slice(text.indexOf('{'), text.lastIndexOf('}') + 1)); } catch { obj = null; }
      if (obj && Array.isArray(obj.captions)) {
        caps = obj.captions.filter(Boolean);
        if (obj.postName && !postName.trim()) setPostName(slug(obj.postName));
        if (obj.campaign && !campaign.trim()) setCampaign(slug(obj.campaign));
      } else {
        try { caps = JSON.parse(text.slice(text.indexOf('['), text.lastIndexOf(']') + 1)); } catch { caps = [text.trim()]; }
      }
      setAiOptions(Array.isArray(caps) ? caps.filter(Boolean) : [String(caps)]);
    } catch (e) { setAiErr(e.message); }
    finally { setAiLoading(false); }
  }

  // Full agentic build: prompt → AI searches the Media Library → fills image,
  // caption, and destination, ready to review + publish.
  async function buildFullPost() {
    if (!aiPrompt.trim() || aiBuilding) return;
    setAiBuilding(true); setAiErr(null); setAiOptions(null);
    try {
      const { proposal, seen, text } = await buildPostWithAI(aiPrompt.trim());
      if (!proposal) { setAiErr(text || 'The AI could not build a post from that.'); return; }
      const asset = seen.get(proposal.mediaAssetId);
      if (asset) { setStoryMode(false); clearPhotos(); setVideoAsset(null); addPhotos([asset]); setMediaType('photo'); setSourceVideo(null); setRepurposeAudio(null); setPostKind('media'); }
      if (proposal.caption) setCaption(proposal.caption);
      if (proposal.postName) setPostName(proposal.postName);           // utm_content (always per-post)
      if (proposal.campaign && !campaign) setCampaign(proposal.campaign); // utm_campaign only if you haven't set one
      if (proposal.destinationPath) {
        const path = proposal.destinationPath.startsWith('/') ? proposal.destinationPath : `/${proposal.destinationPath}`;
        setDestinationUrl(`https://crabplace.com${path}`);
      }
      setPostMsg({ ok: true, text: 'AI built your post — review the image, caption, and link below, then publish.' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { setAiErr(e.message); }
    finally { setAiBuilding(false); }
  }

  function repurpose(v) {
    setStoryMode(false);
    setSourceVideo({ id: v.id, title: v.title });
    setMediaType('video'); setPostKind('media');
    clearPhotos(); setVideoAsset(null);
    setCaption(v.description || '');
    setPostName(v.title ? v.title.slice(0, 60) : 'viral-repost');
    setPostMsg(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Probe FB's downloadable copy for an audio track. Reels often expose a
    // video-only `source` via the API, so the repost would go out SILENT even
    // though nothing in our pipeline strips audio — warn before publishing.
    setRepurposeAudio('checking');
    organicFetch({
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'video-audio-probe', videoId: v.id, brand }),
    })
      .then(r => r.json())
      .then(d => setRepurposeAudio(d.ok && d.hasSource ? (d.hasAudio ? 'ok' : 'muted') : 'unknown'))
      .catch(() => setRepurposeAudio('unknown'));
  }

  // When a repurposed video's FB copy comes back SILENT, look in the Media
  // Library for the uploaded original (which usually carries audio) and offer to
  // swap it in. Runs only for the muted case; any other audio state clears the
  // suggestion. Search failures are silent (console only) — never nag on miss.
  useEffect(() => {
    if (repurposeAudio !== 'muted' || !sourceVideo || !sourceVideo.title) { setOrigSuggest(null); return undefined; }
    let cancelled = false;
    setOrigSuggest({ state: 'searching', candidates: [] });
    findOriginalVideo(sourceVideo.title)
      .then((rows) => {
        if (cancelled) return;
        setOrigSuggest(rows && rows.length ? { state: 'found', candidates: rows } : { state: 'none', candidates: [] });
      })
      .catch(() => { if (!cancelled) setOrigSuggest({ state: 'none', candidates: [] }); });
    return () => { cancelled = true; };
  }, [repurposeAudio, sourceVideo]);

  // Swap in a Media Library video as the post's video (mirrors the picker's
  // onPick video path) and confirm the repost will now have sound.
  function loadOriginalVideo(vid) {
    setStoryMode(false);
    clearPhotos(); setSourceVideo(null); setRepurposeAudio(null); clearFbCover();
    setVideoAsset(vid); setMediaType('video'); setMediaPick('video'); setPostKind('media');
    setOrigSuggest(null);
    setPostMsg({ ok: true, text: `Loaded "${vid.suggested_name || 'the original'}" from your Media Library — this repost will play with sound.` });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Wipe every field back to a clean slate. opts.keepMsg lets the post-success
  // path keep its confirmation banner; the Clear Form button clears that too.
  function resetForm(opts = {}) {
    setCaption(''); clearPhotos(); setVideoAsset(null); setNoSound(false); clearCaptions(); clearFbCover(); setSourceVideo(null); setRepurposeAudio(null); setMediaType('photo'); setMediaPick('photo'); setPostKind('media'); setStoryMode(false);
    setScheduleOn(false); setScheduleAt('');
    setCampaign(''); setPostName(''); setDestinationUrl('https://crabplace.com/');
    setAiPrompt(''); setAiOptions(null); setAiErr(null);
    setUid(Math.random().toString(36).slice(2, 6)); // fresh token for the next post
    if (!opts.keepMsg) setPostMsg(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ── Photo set management (1 = single post, 2-10 = Facebook grid) ──
  const MAX_PHOTOS = 10;
  function addPhotos(assets) {
    const list = Array.isArray(assets) ? assets : [assets];
    setPhotos((prev) => {
      const room = MAX_PHOTOS - prev.length;
      return [...prev, ...list.slice(0, room).map((a) => ({ asset: a, file: null, preview: null }))];
    });
  }
  // Add an image from a pasted URL (Media Library "Copy" link, or any public image URL).
  async function addImageUrl(raw) {
    const url = String(raw || '').trim();
    if (photos.length >= MAX_PHOTOS) { setPostMsg({ ok: false, text: `You can attach up to ${MAX_PHOTOS} photos.` }); return; }
    // A pasted HEIC/HEIF URL can't be displayed by Facebook — fetch the bytes and
    // convert to JPEG server-side (via the Media Library), then attach the result.
    if (/\.(heic|heif)(\?|$)/i.test(url)) {
      setPostMsg(null); setDropBusy(true);
      try {
        const blob = await fetch(url).then((r) => { if (!r.ok) throw new Error(`couldn't fetch it (${r.status})`); return r.blob(); });
        const rawBase64 = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result).slice(String(fr.result).indexOf(',') + 1)); fr.onerror = () => rej(new Error('could not read the file')); fr.readAsDataURL(blob); });
        const res = await fetch(`${SITE_URL}/.netlify/functions/media-library-upload`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: (url.split('/').pop() || 'image.heic').split('?')[0], contentType: 'image/heic', brand: brand === 'crabandcruise' ? 'crab-and-cruise' : 'crab-place', admin_id: user?.id, originalBase64: rawBase64 }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || `upload failed (${res.status})`);
        setUrlInput('');
        addPhotos([data.row || data]);
      } catch (e) {
        setPostMsg({ ok: false, text: `Couldn't add that HEIC URL: ${e.message}. If it's hosted on another site, download it and drag the file in instead.` });
      } finally { setDropBusy(false); }
      return;
    }
    const asset = assetFromUrl(raw);
    if (!asset) { setPostMsg({ ok: false, text: 'That does not look like a valid image URL.' }); return; }
    if (VIDEO_URL_RE.test(asset.r2_original_key || asset.__externalUrl || '')) {
      setPostMsg({ ok: false, text: 'That looks like a video URL. Pick videos from the Media Library so we can process them.' });
      return;
    }
    setPostMsg(null); setUrlInput('');
    addPhotos([asset]);
  }
  // Drag & drop image files onto the media slot. Save-to-Library on → each file
  // goes through the classify/persist media-library-upload pipeline (reusable);
  // off → kept local (size-capped JPEG) and uploaded just-in-time at publish.
  async function addDroppedFiles(fileList) {
    // A dropped video uploads to the Media Library and becomes the post's one video, same as
    // picking it there (Rebecca, Oct 25, 2026: no Library detour first).
    const droppedVideo = !storyMode && Array.from(fileList || []).find(isVideoFile);
    if (droppedVideo) {
      setPostMsg(null); setDropBusy(true);
      try {
        const vid = await uploadVideoToLibrary(droppedVideo, { brand: brand === 'crabandcruise' ? 'crab-and-cruise' : 'crab-place', adminId: user?.id });
        clearPhotos(); setSourceVideo(null); setRepurposeAudio(null); clearFbCover(); setVideoAsset(vid); setMediaType('video'); setMediaPick('video'); setPostKind('media');
      } catch (e) { setPostMsg({ ok: false, text: `Could not add "${droppedVideo.name}": ${e.message}` }); }
      finally { setDropBusy(false); }
      return;
    }
    const files = Array.from(fileList || []).filter((f) => /^image\//.test(f.type) || /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(f.name));
    if (!files.length) { setPostMsg({ ok: false, text: storyMode ? 'Drop an image (JPG, PNG, WEBP, HEIC). To post a video story, pick it from the Media Library.' : 'Drop an image (JPG, PNG, WEBP, HEIC) or a video (MP4, MOV).' }); return; }
    const take = files.slice(0, Math.max(0, MAX_PHOTOS - photos.length));
    if (!take.length) { setPostMsg({ ok: false, text: `You can attach up to ${MAX_PHOTOS} photos.` }); return; }
    setPostMsg(null);
    for (const f of take) {
      setDropBusy(true);
      try {
        if (isHeicFile(f)) {
          // Browser can't decode HEIC — send the raw bytes; the server converts to
          // JPEG + makes the thumbnail. Always lands in the Media Library (there's
          // no local JPEG to keep for the "off" path).
          const rawBase64 = await fileToRawBase64(f);
          const res = await fetch(`${SITE_URL}/.netlify/functions/media-library-upload`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ filename: f.name, contentType: 'image/heic', brand: brand === 'crabandcruise' ? 'crab-and-cruise' : 'crab-place', admin_id: user?.id, originalBase64: rawBase64 }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || `Upload failed (${res.status})`);
          addPhotos([data.row || data]);
        } else if (saveToLibrary) {
          const [orig, thumb] = await Promise.all([imageToBase64(f, 2048, 0.88), imageToBase64(f, 480, 0.78)]);
          const res = await fetch(`${SITE_URL}/.netlify/functions/media-library-upload`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ filename: f.name, contentType: 'image/jpeg', brand: brand === 'crabandcruise' ? 'crab-and-cruise' : 'crab-place', admin_id: user?.id, originalBase64: orig.base64, thumbBase64: thumb.base64, width: orig.naturalWidth, height: orig.naturalHeight }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || `Upload failed (${res.status})`);
          addPhotos([data.row || data]);
        } else {
          const jpeg = await fileToJpegFile(f, 1920, 0.9);
          setPhotos((prev) => (prev.length >= MAX_PHOTOS ? prev : [...prev, { asset: { __localUrl: URL.createObjectURL(jpeg) }, file: jpeg, preview: null }]));
        }
      } catch (e) { setPostMsg({ ok: false, text: `Could not add "${f.name}": ${e.message}` }); }
      finally { setDropBusy(false); }
    }
  }
  function removePhoto(i) {
    setPhotos((prev) => {
      const p = prev[i];
      if (p && p.preview) { try { URL.revokeObjectURL(p.preview); } catch (e) { /* noop */ } }
      return prev.filter((_, idx) => idx !== i);
    });
  }
  function clearPhotos() {
    setPhotos((prev) => { prev.forEach((p) => { if (p.preview) { try { URL.revokeObjectURL(p.preview); } catch (e) { /* noop */ } } }); return []; });
  }
  // Crop confirmed → store the JPEG + instant preview on that photo. Upload happens
  // at publish time (uploadCrop), so the crop always applies (no race).
  function confirmCrop(items) {
    const it = items && items[0];
    const i = cropIdx;
    setCropIdx(null);
    if (i == null) return;
    if (!it || !it.file) { setPostMsg({ ok: false, text: 'Crop failed — the photo could not be loaded.' }); return; }
    setPhotos((prev) => prev.map((p, idx) => {
      if (idx !== i) return p;
      if (p.preview) { try { URL.revokeObjectURL(p.preview); } catch (e) { /* noop */ } }
      return { ...p, file: it.file, preview: URL.createObjectURL(it.file) };
    }));
  }
  function undoCrop(i) {
    setPhotos((prev) => prev.map((p, idx) => {
      if (idx !== i) return p;
      if (p.preview) { try { URL.revokeObjectURL(p.preview); } catch (e) { /* noop */ } }
      return { ...p, file: null, preview: null };
    }));
  }
  // Upload a cropped JPEG to R2 and return its public URL (called inside submit).
  async function uploadCrop(file) {
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(file);
    });
    const base64 = String(dataUrl).split(',')[1];
    const res = await fetch(`${SITE_URL}/.netlify/functions/r2-upload`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: 'organic-crop', contentType: 'image/jpeg', data: base64 }),
    });
    const d = await res.json();
    if (!res.ok || !d.url) throw new Error(d.error || 'Upload failed');
    return d.url;
  }

  // "Post without sound": produce a SILENT copy of a Media Library video via the
  // media-watermark background job (strips the audio track), then return the
  // r2-fetch URL of the silent copy so it posts like any other video. The strip
  // runs in a background fn (beats the ~26s cap) — we poll it the same way the
  // Media Library export tool does. Because this happens BEFORE the post is
  // recorded, a SCHEDULED no-sound post stores the already-silent file, so the
  // cron posts it silently with no extra work.
  async function stripAudioCopy(assetId) {
    const start = await fetch(`${SITE_URL}/.netlify/functions/media-watermark`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'start-video', assetId, stripAudio: true, watermark: false, save: false }),
    }).then(r => r.json());
    if (!start.jobId) throw new Error(start.error || 'Could not start audio removal.');
    // Poll up to ~5 min (background fn); a strip is usually a fast remux.
    for (let i = 0; i < 100; i++) {
      await new Promise(r => setTimeout(r, 3000));
      const d = await fetch(`${SITE_URL}/.netlify/functions/media-watermark`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'job', id: start.jobId }),
      }).then(r => r.json()).catch(() => ({}));
      const job = d.job;
      if (!job) continue;
      if (job.status === 'done') {
        if (!job.out_key) throw new Error('The silent copy produced no file.');
        return `${SITE_URL}/.netlify/functions/r2-fetch?key=${encodeURIComponent(job.out_key)}`;
      }
      if (job.status === 'failed') throw new Error(job.error || 'Audio removal failed.');
    }
    throw new Error('Audio removal timed out — try again.');
  }

  // "Add captions": transcribe the selected video's speech (Whisper, in a
  // background job — beats the ~26s cap) and load the editable segments. The
  // burn itself happens at publish time (captionCopy), mirroring the silent flow.
  async function transcribeVideo() {
    if (!videoAsset || captionState === 'loading') return;
    setCaptionState('loading'); setCaptionErr(null);
    try {
      const start = await fetch(`${SITE_URL}/.netlify/functions/media-watermark`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'start-transcribe', assetId: videoAsset.id }),
      }).then(r => r.json());
      if (!start.jobId) throw new Error(start.error || 'Could not start transcription.');
      for (let i = 0; i < 100; i++) {           // ~5 min (background fn)
        await new Promise(r => setTimeout(r, 3000));
        const d = await fetch(`${SITE_URL}/.netlify/functions/media-watermark`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'transcribe-job', id: start.jobId }),
        }).then(r => r.json()).catch(() => ({}));
        const job = d.job;
        if (!job) continue;
        if (job.status === 'done') {
          const segs = Array.isArray(job.segments) ? job.segments : [];
          if (!segs.length) { setCaptionState('error'); setCaptionErr('No speech was detected in this video.'); return; }
          setCaptionSegments(segs.map(s => ({ start: Number(s.start) || 0, end: Number(s.end) || 0, text: String(s.text || '') })));
          setCaptionsOn(true); setCaptionState('ready'); return;
        }
        if (job.status === 'failed') { setCaptionState('error'); setCaptionErr(job.error || 'Transcription failed.'); return; }
      }
      setCaptionState('error'); setCaptionErr('Transcription timed out — try again.');
    } catch (e) { setCaptionState('error'); setCaptionErr(e.message); }
  }

  // "Caption from my text": burn the caption you TYPED onto the video (no
  // transcription). For b-roll / silent clips — your words, spread across the
  // video, editable afterward like transcribed captions.
  function captionFromMyText() {
    const segs = captionsFromText(caption, videoAsset && videoAsset.duration_s);
    if (!segs.length) {
      setCaptionState('error');
      setCaptionErr('Type your caption in the box above first — that text gets burned onto the video.');
      return;
    }
    setCaptionSegments(segs);
    setCaptionsOn(true); setCaptionState('ready'); setCaptionErr(null);
  }

  // Burn the (possibly edited) caption segments into the video via the
  // media-watermark background job, and return the r2-fetch URL of the captioned
  // copy so it posts like any other video. When silent is on, the burn strips the
  // audio in the same pass. Because this runs BEFORE the post is recorded, a
  // SCHEDULED captioned post stores the already-captioned file (cron posts it).
  async function captionCopy(assetId, segments, silent) {
    const start = await fetch(`${SITE_URL}/.netlify/functions/media-watermark`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'start-video', assetId, captions: segments, stripAudio: !!silent, watermark: false, save: false, captionFont }),
    }).then(r => r.json());
    if (!start.jobId) throw new Error(start.error || 'Could not start captioning.');
    for (let i = 0; i < 200; i++) {             // ~10 min (a caption burn re-encodes)
      await new Promise(r => setTimeout(r, 3000));
      const d = await fetch(`${SITE_URL}/.netlify/functions/media-watermark`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'job', id: start.jobId }),
      }).then(r => r.json()).catch(() => ({}));
      const job = d.job;
      if (!job) continue;
      if (job.status === 'done') {
        if (!job.out_key) throw new Error('The captioned copy produced no file.');
        return `${SITE_URL}/.netlify/functions/r2-fetch?key=${encodeURIComponent(job.out_key)}`;
      }
      if (job.status === 'failed') throw new Error(job.error || 'Captioning failed.');
    }
    throw new Error('Captioning timed out — try again.');
  }

  function updateSegmentText(i, text) { setCaptionSegments(prev => prev.map((s, idx) => (idx === i ? { ...s, text } : s))); }
  function removeSegment(i) { setCaptionSegments(prev => (prev || []).filter((_, idx) => idx !== i)); }
  function clearCaptions() { setCaptionsOn(false); setCaptionSegments(null); setCaptionState('idle'); setCaptionErr(null); }

  // Publish a Facebook Page Story — each frame posts as its own Story, in order.
  // Photo frames post via the backend's 2-step photo_stories flow; video frames
  // are a follow-up (the backend reports them under skippedVideos). Stories carry
  // no caption and can't be scheduled via the API.
  async function publishStory() {
    if (posting) return;
    if (!photos.length) { setPostMsg({ ok: false, text: 'Add at least one Story frame.' }); return; }
    setPosting(true); setPostMsg(null);
    try {
      const mediaItems = [];
      for (const p of photos) {
        if (isVideoAsset(p.asset)) mediaItems.push({ type: 'video', videoKey: p.asset.r2_original_key });
        else mediaItems.push({ type: 'image', url: p.file ? await uploadCrop(p.file) : assetUrl(p.asset) });
      }
      const res = await organicFetch({
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'fb-story-publish', brand, mediaItems, createdBy: user?.staffName || user?.id || null }),
      });
      const d = await readJsonSafe(res);
      if (!d) {
        setPostMsg({ ok: false, text: 'The connection timed out while Facebook processed the Story — some frames may still have posted. Check the Facebook Page before posting again.' });
        return;
      }
      const skipped = Array.isArray(d.skippedVideos) ? d.skippedVideos.length : 0;
      if (d.published || d.count) {
        const n = d.count || 1;
        let text = `Posted ${n} frame${n === 1 ? '' : 's'} to your Facebook Story (visible 24 hours).`;
        if (skipped) text += ` ${skipped} video frame${skipped === 1 ? '' : 's'} skipped — Facebook video Stories are coming soon.`;
        setPostMsg({ ok: true, text });
        clearPhotos();
        loadVideos();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (skipped) {
        setPostMsg({ ok: false, text: 'Facebook video Stories are coming soon. Add at least one photo frame to post a Story now.' });
      } else if (d.needsPermission) {
        setPostMsg({ ok: false, text: d.message });
      } else {
        setPostMsg({ ok: false, text: d.error || d.message || 'Could not post the Story.' });
      }
    } catch (e) { setPostMsg({ ok: false, text: e.message }); }
    finally { setPosting(false); }
  }

  // When the gateway drops the publish response (HTML timeout page), the
  // function almost always finishes server-side — the organic_posts row it
  // inserted flips to 'published' or 'failed'. Poll the list briefly so we can
  // report the truth instead of guessing (and so nobody retries a post that
  // actually went out and double-posts it).
  async function checkPublishOutcome() {
    const me = user?.staffName || user?.id || null;
    for (let i = 0; i < 8; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const r = await organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'list', brand }) });
        const d = await readJsonSafe(r);
        const rows = Array.isArray(d?.posts) ? d.posts : [];
        const mine = rows.find((p) => (!me || p.created_by === me)
          && p.created_at && (Date.now() - new Date(p.created_at).getTime()) < 5 * 60 * 1000);
        if (mine && mine.status === 'published') return { status: 'published', shortLink: mine.short_link };
        if (mine && mine.status === 'failed') return { status: 'failed', error: mine.error };
        // 'publishing' (or not visible yet) → keep polling.
      } catch (e) { /* keep polling */ }
    }
    return { status: 'unknown' };
  }

  async function submit() {
    if (posting) return;
    if (postKind === 'link' && !destinationUrl) { setPostMsg({ ok: false, text: 'Add the link to share (Destination link) for a link post.' }); return; }
    if (postKind !== 'link' && !caption && !photos.length && !sourceVideo && !videoAsset) { setPostMsg({ ok: false, text: 'Add a caption, an image, or a video before posting.' }); return; }
    if (scheduleOn && !scheduleAt) { setPostMsg({ ok: false, text: 'Pick a date/time to schedule, or turn scheduling off.' }); return; }
    setPosting(true); setPostMsg(null);
    // Resolve the media for this post. A post is either a repurposed viral video
    // (sourceVideo), one Media Library video, a photo grid, or text-only.
    let mediaUrls = [];
    let mType = 'none';
    if (sourceVideo) {
      mType = 'video';                       // sourceVideoId path — no urls
    } else if (videoAsset) {
      mType = 'video';
      const segs = (captionsOn && captionSegments)
        ? captionSegments.filter(s => String(s.text || '').trim() && Number(s.end) > Number(s.start))
        : [];
      if (segs.length) {
        // Burn captions first (and strip audio in the same pass if silent). Runs
        // for publish-now AND scheduled, so the stored file is already captioned.
        try {
          setCaptioning(true);
          setPostMsg({ ok: true, text: noSound ? 'Adding captions and removing audio…' : 'Adding captions to the video…' });
          mediaUrls = [await captionCopy(videoAsset.id, segs, noSound)];
        } catch (e) {
          setPostMsg({ ok: false, text: `Could not add captions: ${e.message}` });
          setCaptioning(false); setPosting(false); return;
        }
        setCaptioning(false);
      } else if (noSound) {
        // Strip the audio first so the posted video is silent (publish-now AND
        // scheduled both store the already-silent copy).
        try {
          setStripping(true);
          setPostMsg({ ok: true, text: 'Removing audio from the video…' });
          mediaUrls = [await stripAudioCopy(videoAsset.id)];
        } catch (e) {
          setPostMsg({ ok: false, text: `Could not remove the audio: ${e.message}` });
          setStripping(false); setPosting(false); return;
        }
        setStripping(false);
      } else {
        mediaUrls = [videoFetchUrl(videoAsset)];
      }
    } else if (photos.length) {
      mType = 'photo';
      // Upload each photo's crop if one was made (so the crop always applies),
      // else use the Media Library original.
      try {
        for (const p of photos) mediaUrls.push(p.file ? await uploadCrop(p.file) : assetUrl(p.asset));
      } catch (e) { setPostMsg({ ok: false, text: `Could not upload a cropped photo: ${e.message}` }); setPosting(false); return; }
    }
    // Custom FB video thumbnail: upload the captured frame to R2 (same path as
    // photo crops) so the backend can attach it to the video at publish time.
    let thumbUrl = null;
    if (videoAsset && fbCover && fbCover.file) {
      try { thumbUrl = await uploadCrop(fbCover.file); }
      catch (e) { /* a thumbnail must never block the post — publish without it */ }
    }
    const payload = {
      action: 'publish',
      brand,
      mediaUrl: mediaUrls[0] || null,
      mediaUrls,
      mediaType: mType,
      thumbUrl,
      noSound: !!(videoAsset && noSound),
      caption,
      link: destinationUrl ? link : null,
      utmCampaign: campaign ? slug(campaign) : null,
      sourceVideoId: sourceVideo?.id || null,
      sourceTitle: sourceVideo?.title || null,
      scheduledFor: scheduleOn && scheduleAt ? new Date(scheduleAt).toISOString() : null,
      createdBy: user?.staffName || user?.id || null,
    };
    try {
      const res = await organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      let d = await readJsonSafe(res);
      if (!d) {
        // Gateway timeout page instead of JSON — the publish is still running
        // server-side. Verify the record before saying anything scary.
        setPostMsg({ ok: true, text: 'Facebook is taking a while — checking whether the post went out…' });
        const outcome = await checkPublishOutcome();
        if (outcome.status === 'published') d = { published: true, shortLink: outcome.shortLink };
        else if (outcome.status === 'failed') d = { error: outcome.error || 'Could not post.' };
        else {
          setPostMsg({ ok: false, text: 'The connection timed out while Facebook processed the post — it may still have gone out. Check the Facebook Page (or the Calendar tab) before posting again, so it does not go out twice.' });
          return;
        }
      }
      if (d.scheduled) setPostMsg({ ok: true, text: `Scheduled for ${fmtDate(d.scheduledFor)}.${d.shortLink ? ` Short link: ${d.shortLink}` : ''}` });
      else if (d.published) setPostMsg({ ok: true, text: `Published to Facebook.${d.shortLink ? ` Posted with short link ${d.shortLink}` : ''}` });
      else if (d.needsPermission) setPostMsg({ ok: false, text: d.message });
      else setPostMsg({ ok: false, text: d.error || d.message || 'Could not post.' });
      if (d.scheduled || d.published) {
        resetForm({ keepMsg: true });        // clean slate but keep the success banner
        loadVideos();                        // refresh the viral library (repost counts)
      }
    } catch (e) { setPostMsg({ ok: false, text: e.message }); }
    finally { setPosting(false); }
  }

  return (
    <div>
      {/* Brand switcher */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.3, marginRight: 4 }}>Posting as</span>
        {BRANDS.map((b) => (
          <button key={b.slug} onClick={() => setBrand(b.slug)} disabled={posting}
            style={{ fontSize: 12, fontWeight: 700, padding: '5px 14px', borderRadius: 16, cursor: 'pointer', fontFamily: 'Montserrat', background: brand === b.slug ? 'var(--navy)' : '#fff', color: brand === b.slug ? '#fff' : 'var(--navy)', border: `1px solid ${brand === b.slug ? 'var(--navy)' : 'var(--gray-200)'}` }}>
            {b.name}
          </button>
        ))}
      </div>
      <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 14 }}>
        Create organic Facebook posts with auto-tagged links so their orders show up in <strong>Meta Campaigns → Attributed Orders</strong>. Repost proven viral videos with fresh tags.
      </div>

      {postMsg && (
        <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 6, fontSize: 13, display: 'flex', gap: 8, alignItems: 'flex-start',
          background: postMsg.ok ? '#f0f9f4' : '#fff5f5', border: `1px solid ${postMsg.ok ? '#b7e4c7' : '#ffd6d6'}`, color: postMsg.ok ? '#1a7f37' : '#c33' }}>
          {postMsg.ok ? <CheckCircle2 size={16} style={{ flexShrink: 0 }} /> : <AlertCircle size={16} style={{ flexShrink: 0 }} />}
          <span>{postMsg.text}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Left: compose */}
        <div>
          <Card title="Create a post" right={
            <button type="button" onClick={() => resetForm()} title="Wipe all fields and start fresh"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '4px 10px',
                borderRadius: 14, cursor: 'pointer', fontFamily: 'Montserrat', background: '#fff', border: '1px solid var(--gray-200)', color: 'var(--gray-500)' }}>
              <X size={12} /> Clear form
            </button>
          }>
            {sourceVideo && (
              <div style={{ marginBottom: 12, padding: '8px 10px', background: '#eef6ff', border: '1px solid #bcdcff', borderRadius: 6, fontSize: 12, color: '#1a6fb0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span><Repeat size={12} style={{ verticalAlign: 'middle' }} /> Repurposing viral video: <strong>{sourceVideo.title}</strong></span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <button type="button" onClick={() => watchSourceVideo(sourceVideo.id)} disabled={watchLoading}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, fontFamily: 'Montserrat', padding: '3px 9px', borderRadius: 14, cursor: watchLoading ? 'default' : 'pointer', background: '#fff', color: '#1a6fb0', border: '1px solid #bcdcff' }}>
                      {watchLoading ? <Loader2 size={12} className="spin" /> : <Play size={12} style={{ verticalAlign: 'middle' }} />}
                      {watchLoading ? 'loading…' : 'Watch'}
                    </button>
                    <X size={14} style={{ cursor: 'pointer' }} onClick={() => { setSourceVideo(null); setRepurposeAudio(null); setMediaType('photo'); }} />
                  </div>
                </div>
                {watchError && (
                  <div style={{ marginTop: 5, fontSize: 11, color: '#c33' }}>Preview unavailable for this video.</div>
                )}
                {repurposeAudio === 'checking' && (
                  <div style={{ marginTop: 5, fontSize: 11, color: '#5a7d99' }}>Checking whether Facebook's copy of this video includes the audio track…</div>
                )}
                {repurposeAudio === 'ok' && (
                  <div style={{ marginTop: 5, fontSize: 11, color: '#1a7f37', fontWeight: 600 }}>✓ Audio track present — the repost will play with sound.</div>
                )}
                {repurposeAudio === 'muted' && (
                  <div style={{ marginTop: 6, padding: '6px 8px', background: '#fff8e6', border: '1px solid #ffe9a8', borderRadius: 5, fontSize: 11.5, color: '#7a5b00', lineHeight: 1.5 }}>
                    <strong>⚠ This repost will be silent.</strong> Facebook's API only hands us a video-only copy of this clip (common for Reels) — nothing on our side removes the sound, but there's no audio in the file Facebook provides. To post it WITH sound, use the original file: pick it from the <strong>Media Library</strong> (Video button above) if it's archived there, or upload the original to the Media Library first.
                  </div>
                )}
                {repurposeAudio === 'muted' && origSuggest && origSuggest.state === 'searching' && (
                  <div style={{ marginTop: 6, fontSize: 11, color: '#5a7d99', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Loader2 size={12} className="spin" /> Looking in your Media Library for the original (with sound)…
                  </div>
                )}
                {repurposeAudio === 'muted' && origSuggest && origSuggest.state === 'found' && (
                  <div style={{ marginTop: 6, padding: '8px 10px', background: '#f0f9f4', border: '1px solid #b7e4c7', borderRadius: 5, fontSize: 11.5, color: '#1a7f37', lineHeight: 1.5 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span><Film size={12} style={{ verticalAlign: 'middle' }} /> Found a likely original in your <strong>Media Library</strong> — it should have audio. Use it instead of Facebook's silent copy?</span>
                      <button type="button" onClick={() => setOrigSuggest(null)} title="Dismiss — keep Facebook's silent copy"
                        style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 600, fontFamily: 'Montserrat', padding: '2px 8px', borderRadius: 12, cursor: 'pointer', background: '#fff', color: 'var(--gray-500)', border: '1px solid var(--gray-200)' }}>
                        Keep silent copy
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {origSuggest.candidates.map((vid) => {
                        const thumb = posterUrl(vid);
                        return (
                          <div key={vid.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 6, background: '#fff', border: '1px solid #d4edda', borderRadius: 5 }}>
                            <div style={{ width: 46, height: 46, flexShrink: 0, borderRadius: 4, background: '#000', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {thumb
                                ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                : <Film size={18} color="#fff" style={{ opacity: 0.6 }} />}
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{vid.suggested_name || 'Untitled video'}</div>
                              {vid.alt_text && <div style={{ fontSize: 10.5, color: 'var(--gray-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{vid.alt_text}</div>}
                            </div>
                            <button type="button" onClick={() => loadOriginalVideo(vid)}
                              style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, fontFamily: 'Montserrat', padding: '4px 12px', borderRadius: 14, cursor: 'pointer', background: 'var(--navy)', color: '#fff', border: '1px solid var(--navy)' }}>
                              Use this
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Post type: Photos / Video (each locks the Media Library picker to
                that type — Rebecca's ask) vs a link post (blog/page preview card) */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              {[{ k: 'photo', label: 'Photos' }, { k: 'video', label: 'Video' }, { k: 'story', label: 'Story' }, { k: 'link', label: 'Link (blog / page)' }].map(t => {
                const active = t.k === 'story' ? storyMode
                  : t.k === 'link' ? (postKind === 'link' && !storyMode)
                  : (postKind === 'media' && mediaPick === t.k && !storyMode);
                return (
                  <button key={t.k} type="button"
                    onClick={() => {
                      if (t.k === 'story') {
                        setStoryMode(true); setPostKind('media');
                        setVideoAsset(null); setSourceVideo(null); setRepurposeAudio(null); setNoSound(false); clearCaptions(); clearFbCover();
                      } else if (t.k === 'link') {
                        setStoryMode(false); setPostKind('link');
                        clearPhotos(); setVideoAsset(null); setSourceVideo(null); setRepurposeAudio(null); setNoSound(false); clearCaptions(); clearFbCover(); setMediaType('none');
                      } else {
                        setStoryMode(false); setPostKind('media'); setMediaPick(t.k); setMediaType(t.k);
                      }
                    }}
                    style={{ padding: '6px 14px', fontSize: 12.5, fontWeight: 700, fontFamily: 'Montserrat', borderRadius: 16, cursor: 'pointer',
                      border: `1px solid ${active ? 'var(--navy)' : 'var(--gray-200)'}`, background: active ? 'var(--navy)' : '#fff', color: active ? '#fff' : 'var(--gray-500)' }}>
                    {t.label}
                  </button>
                );
              })}
            </div>

            {storyMode ? (
              <Field label="Story frames">
                <div
                  onDragOver={(e) => { e.preventDefault(); if (!dragActive) setDragActive(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                  onDrop={(e) => { e.preventDefault(); setDragActive(false); addDroppedFiles(e.dataTransfer.files); }}
                  style={{ padding: dragActive ? 8 : 0, borderRadius: 8, outline: dragActive ? '2px dashed var(--navy)' : 'none', outlineOffset: 2, background: dragActive ? 'rgba(0,59,92,0.05)' : 'transparent', transition: 'background 0.12s, padding 0.12s' }}>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    {photos.map((p, i) => {
                      const isVid = isVideoAsset(p.asset);
                      return (
                        <div key={i} style={{ width: 84 }}>
                          <div style={{ position: 'relative', width: 84, height: 84 }}>
                            <img src={isVid ? posterUrl(p.asset) : (p.preview || assetUrl(p.asset))} alt="" style={{ width: 84, height: 84, objectFit: 'contain', borderRadius: 6, border: `1px solid ${p.preview ? '#2e9e5b' : 'var(--gray-200)'}`, background: '#f0f0f3', display: 'block' }} />
                            {isVid && (
                              <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: '50%', background: 'rgba(0,0,0,0.45)' }}>
                                  <Play size={13} fill="#fff" stroke="#fff" />
                                </span>
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginTop: 3 }}>
                            {!isVid && <button style={miniBtn} onClick={() => setCropIdx(i)} title={p.preview ? 'Re-crop' : 'Crop to 9:16'}>{p.preview ? '✓ Crop' : 'Crop'}</button>}
                            {!isVid && p.preview && <button style={miniBtn} onClick={() => undoCrop(i)} title="Undo crop">↺</button>}
                            <button style={miniBtn} onClick={() => removePhoto(i)} title="Remove">✕</button>
                          </div>
                        </div>
                      );
                    })}
                    {photos.length < MAX_PHOTOS && (
                      <button onClick={() => setPickerOpen(true)} title={photos.length ? 'Add another Story frame' : 'Pick images or videos for your Story'}
                        style={{ width: 84, height: 84, borderRadius: 6, border: '1px dashed var(--gray-300)', background: '#fafafa', cursor: 'pointer', color: 'var(--gray-500)', fontSize: 11, fontWeight: 600, fontFamily: 'Montserrat', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        <ImageIcon size={16} />{photos.length ? '+ Add Another' : 'Pick Content'}
                      </button>
                    )}
                  </div>
                  {photos.length < MAX_PHOTOS && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center', maxWidth: 440 }}>
                      <input
                        type="text"
                        value={urlInput}
                        onChange={(e) => setUrlInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addImageUrl(urlInput); } }}
                        placeholder="…or paste an image URL (e.g. the Media Library Copy link)"
                        style={{ flex: 1, padding: '6px 9px', fontSize: 11.5, border: '1px solid var(--gray-200)', borderRadius: 5, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                      <button type="button" onClick={() => addImageUrl(urlInput)} disabled={!urlInput.trim()}
                        style={{ fontSize: 11.5, fontWeight: 600, padding: '6px 13px', borderRadius: 5, border: '1px solid var(--navy)', background: urlInput.trim() ? 'var(--navy)' : '#fff', color: urlInput.trim() ? '#fff' : 'var(--gray-400)', cursor: urlInput.trim() ? 'pointer' : 'default', fontFamily: 'Montserrat' }}>
                        Add
                      </button>
                    </div>
                  )}
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 11.5, color: '#55565a', cursor: 'pointer', userSelect: 'none' }}>
                    <input type="checkbox" checked={saveToLibrary} onChange={(e) => setSaveToLibrary(e.target.checked)} />
                    Save dropped files to the Media Library
                    {dropBusy ? <span style={{ color: 'var(--navy)', fontWeight: 600 }}>· saving…</span> : null}
                  </label>
                  <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 6, lineHeight: 1.5 }}>
                    Each item posts as its own Facebook Story (in order). Facebook Stories are 9:16 and vanish after 24 hours (no caption). Photos post as Stories today; video Stories coming soon.
                  </div>
                </div>
              </Field>
            ) : postKind === 'link' ? (
              <div style={{ marginBottom: 12, padding: '10px 12px', background: '#eef6ff', border: '1px solid #bcdcff', borderRadius: 8, fontSize: 12, color: '#1a6fb0', lineHeight: 1.5 }}>
                <strong>Link post.</strong> No image or video needed. Two things: (1) write your post text in the <strong>Caption</strong> box below, and (2) paste the blog or page URL into <strong>Link to share</strong> further down (replace the default crabplace.com). Facebook shows your caption PLUS a preview card (image + title) pulled from that link. The link is auto-tagged (crabplace.co) so clicks and orders are tracked.
              </div>
            ) : (
            <Field label="Photos or video">
              {sourceVideo ? (
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Using the original viral video. Clear the banner above to pick photos instead.</div>
              ) : videoAsset ? (
                <div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <VideoPreview asset={videoAsset} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <Film size={13} style={{ flexShrink: 0 }} />{videoAsset.suggested_name || 'Video'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
                        {videoAsset.category}/{videoAsset.subcategory}{videoAsset.duration_s ? ` · ${Math.round(videoAsset.duration_s)}s` : ''}
                      </div>
                      <button style={{ ...miniBtn, marginTop: 8 }} onClick={() => { setVideoAsset(null); setNoSound(false); clearFbCover(); setMediaType('photo'); setMediaPick('photo'); }} title="Remove video">✕ Remove video</button>
                    </div>
                  </div>
                  {/* Custom thumbnail — pick the exact frame FB shows before play */}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10 }}>
                    {fbCover ? (
                      <>
                        <img src={fbCover.preview} alt="Chosen thumbnail" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '2px solid #2e9e5b' }} />
                        <div>
                          <div style={{ fontSize: 11.5, fontWeight: 700, color: '#1a7f37' }}>✓ Thumbnail set</div>
                          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                            <button type="button" style={miniBtn} onClick={() => setThumbModalOpen(true)}>Change</button>
                            <button type="button" style={miniBtn} onClick={clearFbCover}>✕ Remove</button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div>
                        <button type="button" style={ghostBtn} onClick={() => setThumbModalOpen(true)}>🖼 Choose thumbnail</button>
                        <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 3 }}>Pick the exact frame Facebook shows before the video plays. Skip it and Facebook picks one for you.</div>
                      </div>
                    )}
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, cursor: 'pointer' }}>
                    <input type="checkbox" checked={noSound} onChange={e => setNoSound(e.target.checked)} />
                    <span style={{ fontSize: 12, color: 'var(--navy)', fontWeight: 600 }}>Post without sound</span>
                  </label>
                  <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 4 }}>
                    {noSound
                      ? 'The audio track is removed before posting, so the Facebook video plays silently. This makes a silent copy first (a few seconds to a minute for large videos).'
                      : 'Posts as a native Facebook video. Remove it to pick photos instead.'}
                  </div>

                  {/* Add captions — transcribe, edit, then burn styled captions in so it reads muted */}
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--gray-100)' }}>
                    {!captionsOn && captionState !== 'loading' && (
                      <>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button type="button" onClick={captionFromMyText}
                            style={{ ...ghostBtn, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <MessageSquare size={13} /> Caption from my text
                          </button>
                          <button type="button" onClick={transcribeVideo}
                            style={{ ...ghostBtn, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <MessageSquare size={13} /> Transcribe speech
                          </button>
                        </div>
                        <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 4 }}>
                          <strong>Caption from my text</strong> burns the caption you typed above onto the video, spread across its length (best for b-roll / silent clips with no one talking). <strong>Transcribe speech</strong> auto-captions a video where someone is speaking. Either way you can edit the lines before publishing, and both pair with "Post without sound."
                        </div>
                      </>
                    )}
                    {captionState === 'loading' && (
                      <div style={{ fontSize: 12, color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Transcribing the video… this can take a minute.
                      </div>
                    )}
                    {captionState === 'error' && (
                      <div style={{ fontSize: 11.5, color: '#c33', marginTop: 4 }}>
                        {captionErr} <button type="button" onClick={transcribeVideo} style={{ ...miniBtn, marginLeft: 4 }}>Try again</button>
                      </div>
                    )}
                    {captionsOn && captionSegments && (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)' }}>Captions ({captionSegments.length}) — edit the wording, timing stays put</span>
                          <button type="button" onClick={clearCaptions} style={miniBtn}>✕ Remove captions</button>
                        </div>
                        <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 6, padding: 8 }}>
                          {captionSegments.map((s, i) => (
                            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 6 }}>
                              <span style={{ fontSize: 10, color: 'var(--gray-500)', fontFamily: 'monospace', whiteSpace: 'nowrap', paddingTop: 6, minWidth: 78 }}>{fmtClock(s.start)}–{fmtClock(s.end)}</span>
                              <textarea rows={1} value={s.text} onChange={e => updateSegmentText(i, e.target.value)} style={{ ...inputStyle, fontSize: 12, resize: 'vertical', padding: '5px 8px' }} />
                              <button type="button" onClick={() => removeSegment(i)} title="Remove this caption" style={{ ...miniBtn, padding: '4px 7px', marginTop: 3 }}>✕</button>
                            </div>
                          ))}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                          <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)' }}>Font</label>
                          <select value={captionFont} onChange={e => setCaptionFont(e.target.value)}
                            style={{ fontSize: 12, padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontFamily: 'Montserrat', color: 'var(--navy)', background: '#fff' }}>
                            {CAPTION_FONTS.map(f => <option key={f.k} value={f.k}>{f.label}</option>)}
                          </select>
                        </div>
                        <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 4 }}>
                          Captions are burned in when you publish (bottom-center, large white text on a dark bar). {noSound ? 'The audio will also be removed.' : 'Turn on "Post without sound" above to also mute it.'}
                        </div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                          <button type="button" onClick={captionFromMyText} style={miniBtn}>↺ Rebuild from my text</button>
                          <button type="button" onClick={transcribeVideo} style={miniBtn}>↺ Re-transcribe</button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  onDragOver={(e) => { e.preventDefault(); if (!dragActive) setDragActive(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                  onDrop={(e) => { e.preventDefault(); setDragActive(false); addDroppedFiles(e.dataTransfer.files); }}
                  style={{ padding: dragActive ? 8 : 0, borderRadius: 8, outline: dragActive ? '2px dashed var(--navy)' : 'none', outlineOffset: 2, background: dragActive ? 'rgba(0,59,92,0.05)' : 'transparent', transition: 'background 0.12s, padding 0.12s' }}>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    {photos.map((p, i) => (
                      <div key={i} style={{ width: 84 }}>
                        <img src={p.preview || assetUrl(p.asset)} alt="" style={{ width: 84, height: 84, objectFit: p.preview ? 'contain' : 'cover', borderRadius: 6, border: `1px solid ${p.preview ? '#2e9e5b' : 'var(--gray-200)'}`, background: '#fff' }} />
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginTop: 3 }}>
                          <button style={miniBtn} onClick={() => setCropIdx(i)} title={p.preview ? 'Re-crop' : 'Crop for Facebook'}>{p.preview ? '✓ Crop' : 'Crop'}</button>
                          {p.preview && <button style={miniBtn} onClick={() => undoCrop(i)} title="Undo crop">↺</button>}
                          <button style={miniBtn} onClick={() => removePhoto(i)} title="Remove">✕</button>
                        </div>
                      </div>
                    ))}
                    {photos.length < MAX_PHOTOS && (
                      <button onClick={() => setPickerOpen(true)} title={mediaPick === 'video' ? 'Pick a video from the Media Library' : 'Add photo(s) from the Media Library'}
                        style={{ width: 84, height: 84, borderRadius: 6, border: '1px dashed var(--gray-300)', background: '#fafafa', cursor: 'pointer', color: 'var(--gray-500)', fontSize: 11, fontWeight: 600, fontFamily: 'Montserrat', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        {mediaPick === 'video' ? <Film size={16} /> : <ImageIcon size={16} />}
                        {photos.length ? 'Add more' : mediaPick === 'video' ? 'Pick video' : 'Pick photos'}
                      </button>
                    )}
                  </div>
                  {photos.length < MAX_PHOTOS && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center', maxWidth: 440 }}>
                      <input
                        type="text"
                        value={urlInput}
                        onChange={(e) => setUrlInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addImageUrl(urlInput); } }}
                        placeholder="…or paste an image URL (e.g. the Media Library Copy link)"
                        style={{ flex: 1, padding: '6px 9px', fontSize: 11.5, border: '1px solid var(--gray-200)', borderRadius: 5, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                      <button type="button" onClick={() => addImageUrl(urlInput)} disabled={!urlInput.trim()}
                        style={{ fontSize: 11.5, fontWeight: 600, padding: '6px 13px', borderRadius: 5, border: '1px solid var(--navy)', background: urlInput.trim() ? 'var(--navy)' : '#fff', color: urlInput.trim() ? '#fff' : 'var(--gray-400)', cursor: urlInput.trim() ? 'pointer' : 'default', fontFamily: 'Montserrat' }}>
                        Add
                      </button>
                    </div>
                  )}
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 11.5, color: '#55565a', cursor: 'pointer', userSelect: 'none' }}>
                    <input type="checkbox" checked={saveToLibrary} onChange={(e) => setSaveToLibrary(e.target.checked)} />
                    Save dropped files to the Media Library
                    {dropBusy ? <span style={{ color: 'var(--navy)', fontWeight: 600 }}>· saving…</span> : null}
                  </label>
                  <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 6 }}>
                    {photos.length >= 2
                      ? `${photos.length} photos → Facebook tiles them in a full-width grid (no borders). Crop each for clean tiles.`
                      : photos.length === 1
                        ? 'One photo = a single post. Add another to make a full-width Facebook grid (no borders). Crop to control framing.'
                        : mediaPick === 'video'
                          ? 'Pick a video from the Media Library — it posts as a native Facebook video, and you can choose its thumbnail frame.'
                          : 'Pick 1 photo for a single post, or 2 to 10 for a full-width Facebook grid.'}
                  </div>
                </div>
              )}
            </Field>
            )}

            {!storyMode && (
            <>
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.3 }}>{`Caption (${caption.length} chars)`}</label>
                <MicButton value={caption} onChange={setCaption} title="Speak your caption instead of typing" />
              </div>
              <textarea rows={5} value={caption} onChange={e => setCaption(e.target.value)} placeholder="Write the post caption, or use the AI assistant below." style={{ ...inputStyle, resize: 'vertical' }} />
            </div>

            {/* AI caption assistant */}
            <div style={{ background: '#faf7ff', border: '1px solid #e7dbff', borderRadius: 6, padding: 10, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', marginBottom: 6 }}><Sparkles size={12} style={{ verticalAlign: 'middle' }} /> AI caption assistant</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                {CAPTION_PRESETS.map(p => (
                  <button key={`b-${p.label}`} type="button" onClick={() => setAiPrompt(p.text)} title={p.text}
                    style={{ fontSize: 11, fontWeight: 600, color: '#7c3aed', background: '#fff', border: '1px solid #d9c7ff', borderRadius: 14, padding: '3px 10px', cursor: 'pointer', fontFamily: 'Montserrat' }}>
                    {p.label}
                  </button>
                ))}
                {savedPresets.map(p => (
                  <span key={p.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: '#7c3aed', background: '#fff', border: '1px solid #d9c7ff', borderRadius: 14, padding: '3px 6px 3px 10px', fontFamily: 'Montserrat' }}>
                    <span style={{ cursor: 'pointer' }} title={p.text} onClick={() => setAiPrompt(p.text)}>{p.label}</span>
                    <X size={11} style={{ cursor: 'pointer', color: '#b9a3e0' }} onClick={() => deletePreset(p.id)} />
                  </span>
                ))}
                <button type="button" onClick={savePreset} title="Save the prompt above as a reusable prefill"
                  style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--gray-500)', background: '#fff', border: '1px dashed #cdd1d6', borderRadius: 14, padding: '3px 10px', cursor: 'pointer', fontFamily: 'Montserrat' }}>
                  💾 Save prompt
                </button>
                <MicButton value={aiPrompt} onChange={setAiPrompt} title="Speak your post idea instead of typing" />
              </div>
              <textarea rows={2} value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} placeholder="Describe the post (e.g. Father's Day crab feast, hero the steamed blue crabs)…" style={{ ...inputStyle, resize: 'vertical', marginBottom: 6 }} />
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button style={{ ...primaryBtn, background: '#7c3aed', padding: '6px 12px' }} onClick={buildFullPost} disabled={aiBuilding || aiLoading}>
                  {aiBuilding ? <><Loader2 size={12} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: 4 }} />Building post…</> : '✨ Build full post (picks image + caption)'}
                </button>
                <button style={{ ...ghostBtn, color: '#7c3aed', borderColor: '#d9c7ff' }} onClick={generateCaption} disabled={aiLoading || aiBuilding}>
                  {aiLoading ? <><Loader2 size={12} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: 4 }} />Writing…</> : 'Captions only'}
                </button>
              </div>
              {aiErr && <div style={{ fontSize: 11, color: '#c33', marginTop: 6 }}>{aiErr}</div>}
              {aiOptions && aiOptions.map((o, i) => (
                <div key={i} onClick={() => setCaption(o)} style={{ marginTop: 6, padding: 8, background: '#fff', border: '1px solid #e7dbff', borderRadius: 5, fontSize: 12, cursor: 'pointer', lineHeight: 1.4 }}>
                  {o}<div style={{ fontSize: 10, color: '#7c3aed', marginTop: 3, fontWeight: 600 }}>Click to use</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><Field label="Campaign / theme" hint="Becomes utm_campaign"><input style={inputStyle} value={campaign} onChange={e => setCampaign(e.target.value)} placeholder="fathers-day-2026" /></Field></div>
              <div style={{ flex: 1 }}><Field label="Post name" hint="Becomes utm_content"><input style={inputStyle} value={postName} onChange={e => setPostName(e.target.value)} placeholder="crab-feast-video" /></Field></div>
            </div>

            <Field label={postKind === 'link' ? 'Link to share — paste the blog / page URL here' : 'Destination link'}>
              <input
                style={postKind === 'link' ? { ...inputStyle, borderColor: '#1877F2', borderWidth: 2 } : inputStyle}
                type="url" value={destinationUrl} onChange={e => setDestinationUrl(e.target.value)}
                placeholder={postKind === 'link' ? 'https://crabplace.com/blog/your-post/' : 'https://crabplace.com/hard-crabs/'} />
            </Field>
            {brand === 'crabplace' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '-2px 0 10px' }}>
                {PRODUCT_LINKS.map((p) => {
                  const full = `https://crabplace.com${p.path}`;
                  const active = destinationUrl === full;
                  return (
                    <button key={p.path} type="button" onClick={() => setDestinationUrl(full)} title={full}
                      style={{ fontSize: 10.5, fontWeight: 600, padding: '3px 10px', borderRadius: 12, cursor: 'pointer', fontFamily: 'Montserrat',
                        background: active ? 'var(--orange)' : '#fff', color: active ? '#fff' : 'var(--navy)', border: `1px solid ${active ? 'var(--orange)' : 'var(--gray-200)'}` }}>
                      {p.label}
                    </button>
                  );
                })}
              </div>
            )}
            {link ? (
              <div style={{ background: '#f5f5f7', padding: 9, borderRadius: 4, marginBottom: 8 }}>
                <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--navy)' }}>🔗 Posts as a short <strong>crabplace.co/…</strong> link (created at publish)</div>
                <div style={{ fontSize: 10, color: 'var(--gray-500)', wordBreak: 'break-all', fontFamily: 'monospace', marginTop: 3 }}>tracks to {link}</div>
              </div>
            ) : (
              <div style={{ fontSize: 10.5, color: 'var(--gray-500)', background: '#f5f5f7', padding: 9, borderRadius: 4, marginBottom: 8 }}>Add a link to preview…</div>
            )}
            {destinationUrl && (tracked ? (
              <div style={{ padding: '7px 10px', background: '#f0f9f4', border: '1px solid #b7e4c7', borderRadius: 4, fontSize: 11, color: '#1a7f37', display: 'flex', gap: 6, marginBottom: 12 }}>
                <CheckCircle2 size={13} style={{ flexShrink: 0 }} /><span><strong>Tracked.</strong> Orders from this post will appear in Meta Campaigns → Attributed Orders.</span>
              </div>
            ) : (
              <div style={{ padding: '7px 10px', background: '#fff8ef', border: '1px solid #ffd9a8', borderRadius: 4, fontSize: 11, color: '#9a5b00', display: 'flex', gap: 6, marginBottom: 12 }}>
                <AlertCircle size={13} style={{ flexShrink: 0 }} /><span><strong>Won't be tracked.</strong> Link isn't on crabplace.com.</span>
              </div>
            ))}

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <input type="checkbox" id="schedOn" checked={scheduleOn} onChange={e => setScheduleOn(e.target.checked)} />
              <label htmlFor="schedOn" style={{ fontSize: 12, color: 'var(--navy)', fontWeight: 600 }}><Calendar size={12} style={{ verticalAlign: 'middle' }} /> Schedule for later</label>
              {scheduleOn && <input type="datetime-local" value={scheduleAt} onChange={e => setScheduleAt(e.target.value)} style={{ ...inputStyle, width: 'auto', flex: 1 }} />}
            </div>
            </>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              {!storyMode && (
                <button style={{ ...ghostBtn, flex: '0 0 auto', padding: '12px 16px', fontSize: 14 }} disabled={posting}
                  title="See how this post will look in the Facebook feed before publishing"
                  onClick={() => setPreviewOpen(true)}>
                  <Eye size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Preview
                </button>
              )}
              <button style={{ ...primaryBtn, flex: 1, padding: 12, fontSize: 15, opacity: posting ? 0.6 : 1 }} onClick={storyMode ? publishStory : submit} disabled={posting || (storyMode && !photos.length)}>
                {posting ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: 6 }} />{captioning ? 'Adding captions…' : stripping ? 'Removing audio…' : 'Working…'}</>
                  : storyMode ? <><Send size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Post to your Story</>
                  : scheduleOn ? <><Calendar size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Schedule post</>
                  : <><Send size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Publish to Facebook</>}
              </button>
            </div>
          </Card>
        </div>

        {/* Right: viral library + history */}
        <div>
          <Card title="Viral video library" right={<button style={ghostBtn} onClick={loadVideos}>Refresh</button>}>
            {videosState === 'loading' && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Loading your top videos…</div>}
            {videosState === 'needsPermission' && (
              <div style={{ fontSize: 12, color: '#9a5b00', background: '#fff8ef', border: '1px solid #ffd9a8', borderRadius: 6, padding: 12, lineHeight: 1.5 }}>
                <AlertCircle size={14} style={{ verticalAlign: 'middle' }} /> {videosMsg}
              </div>
            )}
            {videosState === 'error' && <div style={{ fontSize: 12, color: '#c33' }}>{videosMsg}</div>}
            {videosState === 'ok' && videos && videos.length === 0 && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>No videos found on the page yet.</div>}
            {videosState === 'ok' && videos && videos.length > 0 && (
              <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                {videos.map(v => (
                  <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--gray-100)' }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
                      {v.thumb
                        ? <a href={v.permalink || '#'} target="_blank" rel="noreferrer" style={{ flexShrink: 0, lineHeight: 0 }}><img src={v.thumb} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--gray-200)', background: '#000' }} /></a>
                        : <span style={{ flexShrink: 0, width: 64, height: 64, borderRadius: 6, background: '#eef0f2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Eye size={18} color="#9aa0a6" /></span>}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--navy)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.title}</div>
                        <div style={{ fontSize: 11, color: 'var(--gray-500)', display: 'flex', gap: 10, alignItems: 'center', marginTop: 2 }}>
                          <span><Eye size={11} style={{ verticalAlign: 'middle' }} /> {fmtViews(v.views)} views</span>
                          {(() => {
                            const st = videoAudio[v.id];
                            if (!st) return null;
                            if (st === 'checking') return <span style={{ color: 'var(--gray-400)' }}>checking sound…</span>;
                            if (st === 'ok') return <span title="Facebook's downloadable copy includes the audio track — this repost will play with sound." style={{ color: '#1a8a4a', fontWeight: 700 }}>🔊 Sound</span>;
                            if (st === 'muted') return <span title="Facebook only provides a video-only copy of this clip (common for Reels) — a repost would be silent. Use the original from the Media Library to keep sound." style={{ color: '#b5730a', fontWeight: 700 }}>🔇 Silent</span>;
                            return null;
                          })()}
                          {v.repostCount > 0 && <span style={{ color: v.recentlyReposted ? '#c0392b' : 'var(--gray-500)' }}>reposted {v.repostCount}×</span>}
                        </div>
                        {v.recentlyReposted && <div style={{ fontSize: 10.5, color: '#c0392b', marginTop: 2 }}>⚠ reposted within {overpostDays} days — maybe hold off</div>}
                      </div>
                    </div>
                    <button style={{ ...ghostBtn, flexShrink: 0, color: v.recentlyReposted ? '#c0392b' : 'var(--navy)', borderColor: v.recentlyReposted ? '#f3c0b8' : 'var(--gray-200)' }} onClick={() => repurpose(v)}>
                      <Repeat size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Repurpose
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>

        </div>
      </div>

      {previewOpen && (
        <FbPreviewModal
          brand={brand} caption={caption} postKind={postKind}
          photos={photos} videoAsset={videoAsset} sourceVideo={sourceVideo} fbCover={fbCover}
          destinationUrl={destinationUrl} hasLink={!!link}
          onClose={() => setPreviewOpen(false)} />
      )}
      {pickerOpen && storyMode && (
        <MediaPicker multiSelect
          onClose={() => setPickerOpen(false)} onPick={assets => {
            const list = Array.isArray(assets) ? assets : (assets ? [assets] : []);
            addPhotos(list);   // images + videos both allowed as Story frames
            setPickerOpen(false);
          }} />
      )}
      {pickerOpen && !storyMode && (
        <MediaPicker multiSelect imagesOnly={mediaPick === 'photo'} videosOnly={mediaPick === 'video'}
          onClose={() => setPickerOpen(false)} onPick={assets => {
          const list = Array.isArray(assets) ? assets : [assets];
          const vid = list.find(isVideoAsset);
          if (vid) {
            // A Facebook post is either a photo (grid) or one video — take the first video picked.
            clearPhotos(); setSourceVideo(null); setRepurposeAudio(null); clearFbCover(); setVideoAsset(vid); setMediaType('video'); setMediaPick('video'); setPostKind('media');
          } else {
            setVideoAsset(null); clearFbCover(); addPhotos(list); setMediaType('photo'); setMediaPick('photo'); setSourceVideo(null); setRepurposeAudio(null);
          }
          setPickerOpen(false);
        }} />
      )}
      {thumbModalOpen && videoAsset && (
        <FbThumbModal src={videoFetchUrl(videoAsset)} onSet={(cover) => { clearFbCover(); setFbCover(cover); }} onClose={() => setThumbModalOpen(false)} />
      )}
      {cropIdx != null && photos[cropIdx] && (
        <ImageCropModal
          images={[{ url: cropSourceUrl(photos[cropIdx].asset), name: 'organic-crop.jpg', alt: photos[cropIdx].asset.alt_text || '' }]}
          initialAspect={storyMode ? '9:16' : '1:1'}
          allowAspectChange
          onCancel={() => setCropIdx(null)}
          onConfirm={confirmCrop}
        />
      )}
      {watchUrl && (
        <div onClick={() => setWatchUrl(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(0,0,0,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <button type="button" onClick={() => setWatchUrl(null)} aria-label="Close preview"
            style={{ position: 'absolute', top: 18, right: 22, background: 'rgba(255,255,255,0.14)', color: '#fff', border: 'none', borderRadius: '50%', width: 38, height: 38, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={20} />
          </button>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video src={watchUrl} controls autoPlay playsInline onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: 8, background: '#000' }} />
        </div>
      )}
    </div>
  );
}
